import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DynamicProgress from './LandingPageBuilder/components/DynamicProgress';
import { resolveCountdown, countdownParts, PRE_LAUNCH_LABEL, CAMPAIGN_END_LABEL } from './LandingPageBuilder/countdownUtil';
import DynamicChart from './LandingPageBuilder/components/DynamicChart';
import RecentSubmissionsBlock from '../components/RecentSubmissionsBlock';
import {
  getLabelStyle as fsLabel, getInputStyle as fsInput, getTextareaStyle as fsTextarea,
  getCheckboxInputStyle as fsCheckbox, getCheckboxLabelStyle as fsCheckboxLabel,
  getFieldWrapStyle as fsFieldWrap, getRequiredMarkStyle as fsRequired,
  fieldPlaceholder as fsPlaceholder,
  getSubmitStyle as fsSubmit, getSubmitWrapStyle as fsSubmitWrap,
  CheckboxOption,
} from './LandingPageBuilder/components/formStyles';
import { parseVideoUrl, videoFrameStyle, videoOrientationOf } from './LandingPageBuilder/components/videoEmbed';
import { sanitizeInlineHtml, literalBrToNewline } from './LandingPageBuilder/utils';
import { PannedImage } from './LandingPageBuilder/components/PannedImage';
import { heroBackgroundImage, heroImageFilter, heroImageOpacity, heroTextStyles } from './LandingPageBuilder/heroStyle';
import { GalleryCarousel } from './LandingPageBuilder/GalleryCarousel';
import { galleryArrowProps, galleryCellStyle, galleryFitFor, galleryFits, galleryHasSpans, gallerySpans } from './LandingPageBuilder/galleryLayout';
import { VideoCarousel, videoItemsFromProps } from './LandingPageBuilder/VideoCarousel';
import { backgroundLayerStyle } from './LandingPageBuilder/bgPresets';
import { ShareBlock } from './LandingPageBuilder/ShareBlock';
import PoweredByAmutech from '../components/layout/PoweredByAmutech';
import StudentRegistrationBlock from '../components/StudentRegistrationBlock';
import { InlineHebrewDate } from '../components/common/HebrewDateInput';
import TraineeRegistrationBlock from '../components/TraineeRegistrationBlock';
import { featureFlags } from '../lib/featureFlags';
import { MultiStepNavigation, sortedPages, isMultiPage, resolveBlockPageId, resolvePageSettings, pageTransitionStyle, MULTIPAGE_KEYFRAMES_CSS } from '../components/multipage';
import type { PageSettings } from '../components/multipage';

const API_BASE = '/api';

type DeviceMode = 'desktop' | 'mobile';
type LandingElementType = 'image' | 'text' | 'button' | 'box';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ToolField {
  id: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'address' | 'number' | 'select' | 'checkbox' | 'textarea' | 'date';
  required: boolean;
  options: string[];
  placeholder: string;
}

interface ElementLayout {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface LandingElement {
  id: string;
  type: LandingElementType;
  name?: string;
  content?: string;
  src?: string;
  href?: string;
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
  borderRadius?: number;
  textAlign?: 'right' | 'center' | 'left';
  objectFit?: 'cover' | 'contain';
  layouts?: Partial<Record<DeviceMode, ElementLayout>>;
}

interface LandingCanvas {
  width: number;
  minHeight: number;
  backgroundColor: string;
}

interface LandingDesign {
  canvas?: Partial<Record<DeviceMode, Partial<LandingCanvas>>>;
  elements?: LandingElement[];
}

interface SigBlockStyle {
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  color?: string;
  underline?: boolean;
  italic?: boolean;
  align?: 'right' | 'center' | 'left';
}
interface SigDocBlock {
  id: string;
  type: 'heading' | 'paragraph' | 'checkbox' | 'signature' | 'spacer' | 'image';
  text?: string;
  /** Same content as `text` with per-selection formatting (bold/color/size on
   *  part of the line) and fields as `<span data-field-id>`. Absent on
   *  paragraphs that were never formatted — those still render from `text`. */
  html?: string;
  level?: 1 | 2;
  required?: boolean;
  signerLabel?: string;
  style?: SigBlockStyle;
  src?: string;
  width?: number;
  align?: 'right' | 'center' | 'left';
}
interface SigInlineField {
  id: string;
  label: string;
  type: 'text' | 'date' | 'number' | 'idNumber' | 'select';
  required: boolean;
  width?: number;
  /** date fields only — which calendar the signer fills in. Default: לועזי. */
  calendar?: 'greg' | 'hebrew' | 'both';
  /** select fields only — the choices offered to the signer. */
  options?: { id: string; label: string }[];
  /** true = value is decided by the org, shown as plain text (not editable). */
  readOnly?: boolean;
}

interface PublicToolData {
  _id: string;
  name: string;
  type: 'form' | 'landing' | 'signature';
  slug: string;
  title: string;
  description: string;
  fields: ToolField[];
  submitButtonText: string;
  successMessage: string;
  redirectUrl: string;
  heroImage: string;
  heroTitle: string;
  heroSubtitle: string;
  bodyContent: string;
  ctaText: string;
  ctaUrl: string;
  ctaStyle: 'button' | 'form';
  primaryColor: string;
  logoUrl: string;
  landingDesign?: LandingDesign;
  documentBlocks?: SigDocBlock[];
  inlineFields?: SigInlineField[];
  titleStyle?: SigBlockStyle;
  descriptionStyle?: SigBlockStyle;
  logoHeight?: number;
  logoAlign?: 'right' | 'center' | 'left';
  docBgColor?: string;
  docBgImage?: string;
  /** Tuition registration forms: how the class (כיתה/שכבה) is decided. */
  regClassMode?: 'free' | 'choice' | 'fixed';
  regFixedUnitId?: string | null;
  /** Server-resolved (GET /public/tools/:slug) — never authored in the doc. */
  regClassOptions?: { id: string; label: string }[];
  regFixedUnitName?: string;
  /** Payment track (מסלול תשלום) offered on the form. */
  regPlanMode?: 'none' | 'choice' | 'fixed';
  regFixedPlanTemplateId?: string | null;
  regPlanOptions?: { id: string; label: string }[];
  /** true when the institution collects payment right after signing */
  regPaymentEnabled?: boolean;
  /** 'first' = the first parent pays; 'choice' = the signer picks / splits */
  regPayerMode?: 'first' | 'choice' | 'student';
  regPayerAllowStudent?: boolean;
  regFixedPlanLabel?: string;
}

const DEFAULT_CANVAS: Record<DeviceMode, LandingCanvas> = {
  desktop: { width: 1200, minHeight: 900, backgroundColor: '#ffffff' },
  mobile: { width: 390, minHeight: 844, backgroundColor: '#ffffff' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const useDeviceMode = (): DeviceMode => {
  const getMode = (): DeviceMode => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop');
  const [mode, setMode] = useState<DeviceMode>(getMode);

  useEffect(() => {
    const onResize = () => setMode(getMode());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return mode;
};

const getCanvasForMode = (tool: PublicToolData, mode: DeviceMode): LandingCanvas => {
  const canvas = tool.landingDesign?.canvas?.[mode];
  return {
    width: canvas?.width || DEFAULT_CANVAS[mode].width,
    minHeight: canvas?.minHeight || DEFAULT_CANVAS[mode].minHeight,
    backgroundColor: canvas?.backgroundColor || DEFAULT_CANVAS[mode].backgroundColor,
  };
};

const getElementLayout = (element: LandingElement, mode: DeviceMode): ElementLayout | null => {
  const fallback = mode === 'mobile' ? element.layouts?.desktop : element.layouts?.mobile;
  const layout = element.layouts?.[mode] || fallback;

  if (!layout) return null;

  return {
    x: layout.x ?? 0,
    y: layout.y ?? 0,
    width: layout.width ?? 200,
    height: layout.height ?? 80,
  };
};

const hasLandingDesign = (tool: PublicToolData): boolean => {
  return Boolean(tool.landingDesign?.elements?.length);
};

// ── Field Input ───────────────────────────────────────────────────────────────

const FieldInput: React.FC<{
  field: ToolField;
  value: any;
  onChange: (v: any) => void;
  primary: string;
  readOnly?: boolean;
}> = ({ field, value, onChange, primary, readOnly }) => {
  const base = "w-full px-4 py-3 bg-white border border-gray-200 text-gray-800 placeholder-gray-400 rounded-xl focus:outline-none transition-all text-right";
  const focusStyle = { outlineColor: primary };

  if (field.type === 'checkbox') {
    return (
      <label className={`flex items-center gap-3 select-none ${readOnly ? '' : 'cursor-pointer'}`}>
        <div
          onClick={readOnly ? undefined : () => onChange(!value)}
          className="w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all flex-shrink-0"
          style={value ? { background: primary, borderColor: primary } : { borderColor: '#d1d5db' }}
        >
          {value && (
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <span className="text-gray-700 text-sm">{field.label}{field.required && <span className="text-red-500 mr-0.5">*</span>}</span>
      </label>
    );
  }

  if (field.type === 'select') {
    return (
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        required={field.required}
        disabled={readOnly}
        className={base}
        style={focusStyle}
      >
        <option value="">בחר אפשרות...</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  if (field.type === 'textarea') {
    return (
      <textarea
        rows={4}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={fsPlaceholder(field) || field.label}
        required={field.required}
        readOnly={readOnly}
        className={base}
        style={focusStyle}
      />
    );
  }

  const typeMap: Record<string, string> = {
    text: 'text', email: 'email', phone: 'tel', address: 'text', number: 'number', date: 'date',
  };

  return (
    <input
      type={typeMap[field.type] || 'text'}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={fsPlaceholder(field) || field.label}
      required={field.required}
      readOnly={readOnly}
      autoComplete={field.type === 'address' ? 'street-address' : undefined}
      className={base}
      style={focusStyle}
      min={field.type === 'number' && typeof (field as any).minValue === 'number' ? (field as any).minValue : undefined}
      max={field.type === 'number' && typeof (field as any).maxValue === 'number' ? (field as any).maxValue : undefined}
    />
  );
};

// ── Form component ────────────────────────────────────────────────────────────

// Read PDF prefill / print mode.
// Two channels: window.__PREFILL_VALUES__ (preferred — set by Puppeteer via
// evaluateOnNewDocument, avoids HTTP 431 when submissions include signature
// PNG data URLs), and the legacy ?prefill= base64-JSON URL param.
// The mailing appends ?mc=<mailCampaignId> to the landing-page link; forward it
// on submit so the registration is attributed to that mailing campaign.
function getMcParam(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const mc = new URLSearchParams(window.location.search).get('mc');
  return mc && /^[a-f0-9]{24}$/i.test(mc) ? mc : undefined;
}

function getUrlPrefill(): { values: Record<string, any> | null; printMode: boolean } {
  if (typeof window === 'undefined') return { values: null, printMode: false };
  const params = new URLSearchParams(window.location.search);
  const printMode = params.get('print') === '1' || params.get('printMode') === '1';
  const injected = (window as any).__PREFILL_VALUES__;
  if (injected && typeof injected === 'object') {
    return { values: injected, printMode };
  }
  const raw = params.get('prefill');
  if (!raw) return { values: null, printMode };
  try {
    const decoded = decodeURIComponent(escape(atob(raw)));
    return { values: JSON.parse(decoded), printMode };
  } catch {
    return { values: null, printMode };
  }
}

/**
 * Ambassador signup result — returned by POST /public/tools/:slug/submit when
 * the form is linked to a campaign (Tool.ambassadorCampaignId). Absent for
 * every plain form, so nothing below renders for them.
 */
export interface AmbassadorResult {
  slug: string;
  name: string;
  shareUrl: string;
  dashboardUrl: string;
  campaignName: string;
  alreadyRegistered: boolean;
  /** Name of the ambassador group they were assigned to ('' = none). */
  groupName?: string;
}

/**
 * Post-submit panel for ambassador-signup forms: hands the new ambassador
 * their personal share link (the whole point of the form) plus a link to their
 * live dashboard. Rendered inside every success screen.
 */
const AmbassadorSharePanel: React.FC<{ amb: AmbassadorResult; primary: string }> = ({ amb, primary }) => {
  const [copied, setCopied] = useState(false);
  const shareText = `${amb.campaignName ? `הצטרפתי לגיוס עבור ${amb.campaignName}! ` : ''}אשמח לעזרתכם: ${amb.shareUrl}`;

  return (
    <div dir="rtl" style={{
      marginTop: 20, textAlign: 'right', background: '#f0fdf4',
      border: '1px solid #bbf7d0', borderRadius: 16, padding: 16,
    }}>
      <p style={{ fontSize: 15, fontWeight: 800, color: '#065f46', margin: '0 0 4px' }}>
        🤝 {amb.alreadyRegistered ? `${amb.name}, אתם כבר רשומים כשגריר` : `${amb.name}, אתם עכשיו שגריר!`}
        {amb.campaignName ? ` — ${amb.campaignName}` : ''}
      </p>
      {!!amb.groupName && (
        <p style={{ fontSize: 13, fontWeight: 700, color: '#047857', margin: '0 0 4px' }}>
          👥 {amb.alreadyRegistered ? 'אתם בקבוצת' : 'הצטרפתם לקבוצת'} "{amb.groupName}"
        </p>
      )}
      <p style={{ fontSize: 12.5, color: '#047857', margin: '0 0 10px', lineHeight: 1.6 }}>
        זה הלינק האישי שלכם. כל תרומה שתגיע דרכו תיזקף לזכותכם.
      </p>
      <div style={{
        direction: 'ltr', textAlign: 'left', background: '#fff', border: '1px solid #d1fae5',
        borderRadius: 10, padding: '10px 12px', fontSize: 12.5, wordBreak: 'break-all', color: '#111827',
      }}>
        {amb.shareUrl}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard?.writeText(amb.shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          style={{
            flex: '1 1 120px', padding: '10px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: primary, color: '#fff', fontSize: 13.5, fontWeight: 700,
          }}
        >
          {copied ? '✓ הועתק' : 'העתק לינק'}
        </button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
          target="_blank"
          rel="noreferrer"
          style={{
            flex: '1 1 120px', padding: '10px 14px', borderRadius: 10, textAlign: 'center',
            background: '#25D366', color: '#fff', fontSize: 13.5, fontWeight: 700, textDecoration: 'none',
          }}
        >
          שיתוף בוואטסאפ
        </a>
      </div>
      <a
        href={amb.dashboardUrl}
        target="_blank"
        rel="noreferrer"
        style={{ display: 'inline-block', marginTop: 10, fontSize: 12.5, fontWeight: 700, color: '#047857' }}
      >
        📊 לדאשבורד השגריר שלי →
      </a>
    </div>
  );
};

/**
 * "הטופס נשלח בהצלחה" as a centered modal.
 *
 * The blocks renderer used to swap the FORM BLOCK's content for a success
 * message, which lands wherever that block happens to sit on the page —
 * usually far down, so the submitter is left staring at a confirmation
 * halfway down a page they've already scrolled past. This dialog is anchored
 * to the viewport (portal → body, so the canvas `zoom` wrapper can't drag it
 * along), locks the page behind it and closes on Esc / backdrop / button.
 */
const SUCCESS_MODAL_CSS = `
@keyframes amtSuccessFade { from { opacity: 0 } to { opacity: 1 } }
@keyframes amtSuccessPop {
  0%   { opacity: 0; transform: translateY(18px) scale(0.94) }
  100% { opacity: 1; transform: translateY(0) scale(1) }
}
@keyframes amtSuccessRing { 0% { transform: scale(0.4); opacity: 0 } 60% { transform: scale(1.08) } 100% { transform: scale(1); opacity: 1 } }
@keyframes amtSuccessCheck { from { stroke-dashoffset: 32 } to { stroke-dashoffset: 0 } }
@media (prefers-reduced-motion: reduce) {
  .amt-success-backdrop, .amt-success-card, .amt-success-ring, .amt-success-check { animation: none !important }
}
`;

const FormSuccessModal: React.FC<{
  open: boolean;
  onClose: () => void;
  primary: string;
  title?: string;
  message?: string;
  logoUrl?: string;
  /** true while a redirect is pending — the modal says so instead of pretending it's done. */
  redirecting?: boolean;
  ambassador?: AmbassadorResult | null;
}> = ({ open, onClose, primary, title, message, logoUrl, redirecting, ambassador }) => {
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      dir="rtl"
      className="amt-success-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2147483000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)',
        animation: 'amtSuccessFade 0.18s ease-out both',
        fontFamily: "'Heebo', sans-serif",
      }}
    >
      <style>{SUCCESS_MODAL_CSS}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title || 'הטופס נשלח בהצלחה'}
        className="amt-success-card"
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative', width: '100%', maxWidth: 420,
          maxHeight: '88vh', overflowY: 'auto',
          background: '#fff', borderRadius: 24, padding: '32px 26px 26px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.28)', textAlign: 'center',
          animation: 'amtSuccessPop 0.28s cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        <button
          onClick={onClose}
          aria-label="סגירה"
          style={{
            position: 'absolute', top: 12, insetInlineStart: 12,
            width: 30, height: 30, borderRadius: '50%', border: 'none',
            background: '#f3f4f6', color: '#6b7280', fontSize: 16, lineHeight: 1,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >×</button>

        {logoUrl && (
          <img src={logoUrl} alt="לוגו" style={{ height: 40, objectFit: 'contain', margin: '0 auto 14px', display: 'block' }} />
        )}

        <div
          className="amt-success-ring"
          style={{
            width: 76, height: 76, borderRadius: '50%', margin: '0 auto 18px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${primary}, ${primary}BB)`,
            boxShadow: `0 10px 26px ${primary}55`,
            animation: 'amtSuccessRing 0.42s cubic-bezier(0.16,1,0.3,1) both',
          }}
        >
          <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
            <path className="amt-success-check" d="M5 13l4 4L19 7" style={{ strokeDasharray: 32, animation: 'amtSuccessCheck 0.4s 0.18s ease-out both' }} />
          </svg>
        </div>

        <h2 style={{ fontSize: 23, fontWeight: 900, color: '#111827', margin: '0 0 8px' }}>
          {title || 'הטופס נשלח בהצלחה!'} 🎉
        </h2>
        <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.7, margin: 0 }}>
          {message || 'תודה! הפרטים התקבלו אצלנו.'}
        </p>

        {ambassador && <AmbassadorSharePanel amb={ambassador} primary={primary} />}

        {redirecting ? (
          <p style={{ fontSize: 12.5, color: '#9ca3af', marginTop: 18 }}>מעבירים אתכם לעמוד הבא...</p>
        ) : (
          <button
            onClick={onClose}
            style={{
              marginTop: 22, width: '100%', padding: '13px 20px', borderRadius: 14, border: 'none',
              background: primary, color: '#fff', fontSize: 15.5, fontWeight: 800, cursor: 'pointer',
              boxShadow: `0 8px 20px ${primary}44`, fontFamily: 'inherit',
            }}
          >סגירה</button>
        )}
      </div>
    </div>,
    document.body,
  );
};

const ToolForm: React.FC<{
  tool: PublicToolData;
  primary: string;
  onSuccess: (ambassador?: AmbassadorResult | null) => void;
}> = ({ tool, primary, onSuccess }) => {
  const { values: prefill, printMode } = React.useMemo(getUrlPrefill, []);
  const [values, setValues] = useState<Record<string, any>>(prefill || {});
  const [submitting, setSubmitting] = useState(false);
  // Built-in marketing-consent checkbox (unchecked by default — opt-in per
  // תיקון 13). Sent as a top-level flag, stamped onto the Contact server-side.
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (printMode) return;
    setError(null);
    try {
      setSubmitting(true);
      const res = await axios.post(`${API_BASE}/public/tools/${tool.slug}/submit`, { data: values, mc: getMcParam(), marketingConsent });
      onSuccess(res.data?.ambassador || null);
      // Ambassador signups never auto-redirect — the personal share link IS
      // the result of the form, and bouncing the page away would lose it.
      if (tool.redirectUrl && !res.data?.ambassador) {
        setTimeout(() => { window.location.href = tool.redirectUrl; }, 1500);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'אירעה שגיאה בשליחה. נסה שוב.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" dir="rtl">
      {tool.fields.map(field => (
        <div key={field.id}>
          {field.type !== 'checkbox' && (
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {field.label}
              {field.required && <span className="text-red-500 mr-1">*</span>}
            </label>
          )}
          <FieldInput
            field={field}
            value={values[field.id]}
            onChange={v => setValues(prev => ({ ...prev, [field.id]: v }))}
            primary={primary}
            readOnly={printMode}
          />
        </div>
      ))}

      {!printMode && (
        <label className="flex items-start gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={marketingConsent}
            onChange={e => setMarketingConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0"
          />
          <span>אני מאשר/ת קבלת עדכונים והודעות (אפשר להסיר את עצמך בכל עת)</span>
        </label>
      )}
      {/* חובת יידוע בעת איסוף מידע (סעיף 11 לחוק הגנת הפרטיות) */}
      <p className="text-xs text-gray-400 leading-relaxed">
        הפרטים הנמסרים בטופס יישמרו אצל הארגון מפעיל הטופס לצורך הטיפול בפנייתך, בהתאם ל
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">מדיניות הפרטיות</a>.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm">
          ⚠️ {error}
        </div>
      )}

      {!printMode && (
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-4 rounded-2xl font-bold text-lg text-white transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
          style={!submitting ? { background: `linear-gradient(135deg, ${primary}, ${primary}CC)`, boxShadow: `0 6px 20px ${primary}40` } : { background: '#9CA3AF' }}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              שולח...
            </span>
          ) : (
            tool.submitButtonText || 'שלח'
          )}
        </button>
      )}
    </form>
  );
};

const LandingDesignRenderer: React.FC<{
  tool: PublicToolData;
  primary: string;
  submitted: boolean;
  onSuccess: (ambassador?: AmbassadorResult | null) => void;
  /** Ambassador-signup result, when this form is linked to a campaign. */
  ambassador?: AmbassadorResult | null;
}> = ({ tool, primary, submitted, onSuccess, ambassador }) => {
  const deviceMode = useDeviceMode();
  const canvas = useMemo(() => getCanvasForMode(tool, deviceMode), [tool, deviceMode]);
  const showInlineForm = tool.ctaStyle === 'form';

  return (
    <div
      className="w-full"
      style={{
        background: canvas.backgroundColor,
        minHeight: canvas.minHeight,
      }}
    >
      <div
        className="relative mx-auto w-full"
        style={{
          maxWidth: deviceMode === 'desktop' ? canvas.width : '100%',
          minHeight: canvas.minHeight,
        }}
      >
        {(tool.landingDesign?.elements || []).map((element) => {
          const layout = getElementLayout(element, deviceMode);
          if (!layout) return null;

          const commonStyle: React.CSSProperties = {
            position: 'absolute',
            left: layout.x,
            top: layout.y,
            width: layout.width,
            height: layout.height,
            borderRadius: element.borderRadius || 0,
            overflow: 'hidden',
          };

          if (element.type === 'image') {
            if (!element.src) return null;
            return (
              <img
                key={element.id}
                src={element.src}
                alt={element.name || 'landing image'}
                style={{
                  ...commonStyle,
                  objectFit: element.objectFit || 'cover',
                }}
              />
            );
          }

          if (element.type === 'text') {
            return (
              <div
                key={element.id}
                style={{
                  ...commonStyle,
                  color: element.color || '#111827',
                  fontSize: element.fontSize || 24,
                  textAlign: element.textAlign || 'right',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent:
                    element.textAlign === 'center'
                      ? 'center'
                      : element.textAlign === 'left'
                        ? 'flex-start'
                        : 'flex-end',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.4,
                }}
              >
                <div className="w-full">{element.content}</div>
              </div>
            );
          }

          if (element.type === 'button') {
            const buttonContent = (
              <div
                className="w-full h-full flex items-center justify-center font-bold px-4 text-center"
                style={{
                  background: element.backgroundColor || primary,
                  color: element.color || '#ffffff',
                  fontSize: element.fontSize || 18,
                  borderRadius: element.borderRadius || 16,
                }}
              >
                {element.content || tool.ctaText || 'לחץ כאן'}
              </div>
            );

            if (showInlineForm) {
              return <div key={element.id} style={commonStyle}>{buttonContent}</div>;
            }

            return (
              <a
                key={element.id}
                href={element.href || tool.ctaUrl || '#content'}
                target={element.href || tool.ctaUrl ? '_blank' : undefined}
                rel={element.href || tool.ctaUrl ? 'noopener noreferrer' : undefined}
                style={{ ...commonStyle, display: 'block' }}
              >
                {buttonContent}
              </a>
            );
          }

          return (
            <div
              key={element.id}
              style={{
                ...commonStyle,
                background: element.backgroundColor || '#F3F4F6',
              }}
            />
          );
        })}
      </div>

      {tool.bodyContent && (
        <div id="content" className="max-w-3xl mx-auto px-6 py-12">
          <div
            className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: tool.bodyContent }}
          />
        </div>
      )}

      {showInlineForm && !submitted && (
        <div className="max-w-lg mx-auto px-4 pb-12">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
            <div className="px-8 pt-8 pb-5" style={{ background: `linear-gradient(135deg, ${primary}15, ${primary}05)` }}>
              <h2 className="text-xl font-black text-gray-900">{tool.ctaText || tool.title || 'הצטרף עכשיו'}</h2>
              {tool.description && <p className="text-gray-500 text-sm mt-1">{tool.description}</p>}
            </div>
            <div className="px-8 pb-8 pt-5">
              <ToolForm tool={tool} primary={primary} onSuccess={onSuccess} />
            </div>
          </div>
        </div>
      )}

      {showInlineForm && submitted && (
        <div className="max-w-lg mx-auto px-4 pb-12">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: `linear-gradient(135deg, ${primary}, ${primary}CC)` }}>
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-black text-gray-800 mb-2">נשלח בהצלחה! 🎉</h3>
            <p className="text-gray-500">{tool.successMessage || 'תודה! הטופס התקבל בהצלחה.'}</p>
            {ambassador && <AmbassadorSharePanel amb={ambassador} primary={primary} />}
          </div>
        </div>
      )}
    </div>
  );
};

const LegacyLandingRenderer: React.FC<{
  tool: PublicToolData;
  primary: string;
  submitted: boolean;
  onSuccess: (ambassador?: AmbassadorResult | null) => void;
  /** Ambassador-signup result, when this form is linked to a campaign. */
  ambassador?: AmbassadorResult | null;
}> = ({ tool, primary, submitted, onSuccess, ambassador }) => {
  const showInlineForm = tool.ctaStyle === 'form';

  return (
    <>
      <div className="relative w-full overflow-hidden" style={{ minHeight: 400 }}>
        {tool.heroImage ? (
          <img src={tool.heroImage} alt="hero" className="w-full h-[450px] object-cover" />
        ) : (
          <div className="w-full h-[350px]" style={{ background: `linear-gradient(135deg, ${primary}, ${primary}AA, #1E40AF)` }} />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.6) 100%)' }} />

        <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-6 py-16">
          {tool.logoUrl && (
            <img src={tool.logoUrl} alt="לוגו" className="h-16 object-contain mb-5 drop-shadow-lg" />
          )}
          {tool.heroTitle && (
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight drop-shadow-xl max-w-3xl"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}>
              {tool.heroTitle}
            </h1>
          )}
          {tool.heroSubtitle && (
            <p className="text-white/80 text-lg sm:text-xl mt-4 max-w-2xl leading-relaxed drop-shadow">
              {tool.heroSubtitle}
            </p>
          )}
          {tool.ctaText && !showInlineForm && (
            <a
              href={tool.ctaUrl || '#content'}
              className="mt-8 inline-block px-10 py-4 rounded-2xl font-bold text-xl text-white shadow-2xl hover:scale-105 transition-all duration-200"
              style={{ background: `linear-gradient(135deg, ${primary}, ${primary}CC)`, boxShadow: `0 8px 30px ${primary}50` }}
            >
              {tool.ctaText}
            </a>
          )}
        </div>
      </div>

      {tool.bodyContent && (
        <div id="content" className="max-w-3xl mx-auto px-6 py-12">
          <div
            className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: tool.bodyContent }}
          />
        </div>
      )}

      {showInlineForm && !submitted && (
        <div className="max-w-lg mx-auto px-4 pb-12">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
            <div className="px-8 pt-8 pb-5" style={{ background: `linear-gradient(135deg, ${primary}15, ${primary}05)` }}>
              <h2 className="text-xl font-black text-gray-900">{tool.ctaText || tool.title || 'הצטרף עכשיו'}</h2>
              {tool.description && <p className="text-gray-500 text-sm mt-1">{tool.description}</p>}
            </div>
            <div className="px-8 pb-8 pt-5">
              <ToolForm tool={tool} primary={primary} onSuccess={onSuccess} />
            </div>
          </div>
        </div>
      )}

      {showInlineForm && submitted && (
        <div className="max-w-lg mx-auto px-4 pb-12">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: `linear-gradient(135deg, ${primary}, ${primary}CC)` }}>
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-black text-gray-800 mb-2">נשלח בהצלחה! 🎉</h3>
            <p className="text-gray-500">{tool.successMessage || 'תודה! הטופס התקבל בהצלחה.'}</p>
            {ambassador && <AmbassadorSharePanel amb={ambassador} primary={primary} />}
          </div>
        </div>
      )}

      {!showInlineForm && tool.ctaText && tool.ctaUrl && (
        <div className="text-center py-12">
          <a
            href={tool.ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-12 py-4 rounded-2xl font-bold text-xl text-white shadow-xl hover:scale-105 transition-all duration-200"
            style={{ background: `linear-gradient(135deg, ${primary}, ${primary}CC)`, boxShadow: `0 6px 25px ${primary}40` }}
          >
            {tool.ctaText}
          </a>
        </div>
      )}
    </>
  );
};

// ── New Blocks Renderer (for LandingPageBuilder blocks) ─────────────────────

interface BuilderBlock {
  id: string;
  type: string;
  content?: string;
  props?: Record<string, any>;
  style?: Record<string, any>;
  mobileStyle?: Record<string, any>;
  mobileProps?: Record<string, any>;
  desktopLayout?: { x: number; y: number; width: number; height: number };
  mobileLayout?: { x: number; y: number; width: number; height: number };
  hiddenOnMobile?: boolean;
  hiddenOnDesktop?: boolean;
  pageId?: string;
}

interface ToolPageDef {
  id: string;
  name: string;
  order: number;
}

const isBlockVisibleInDevice = (block: BuilderBlock, device: DeviceMode): boolean => {
  if (device === 'mobile' && block.hiddenOnMobile) return false;
  if (device === 'desktop' && block.hiddenOnDesktop) return false;
  return true;
};

const hasNewBlocks = (tool: any): boolean => {
  return Array.isArray(tool.blocks) && tool.blocks.length > 0 && (tool.blocks[0]?.desktopLayout || tool.blocks[0]?.mobileLayout);
};

const SignatureCanvas: React.FC<{
  value: string;
  onChange: (dataUrl: string | null) => void;
  props: any;
  width: number;
  height: number;
  printMode: boolean;
}> = ({ value, onChange, props: p, width, height, printMode }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const [hasSignature, setHasSignature] = useState(!!value);

  const labelHeight = (p.labelFontSize || 14) + 12;
  const clearHeight = 28;
  const canvasH = Math.max(80, height - labelHeight - clearHeight);

  // Restore from value (e.g. prefill) once on mount
  useEffect(() => {
    if (!value || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setHasSignature(true);
    };
    img.src = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      const t = e.touches[0] || e.changedTouches[0];
      return { x: (t.clientX - rect.left) * scaleX, y: (t.clientY - rect.top) * scaleY };
    }
    const m = e as React.MouseEvent;
    return { x: (m.clientX - rect.left) * scaleX, y: (m.clientY - rect.top) * scaleY };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    if (printMode) return;
    e.preventDefault();
    isDrawingRef.current = true;
    lastPosRef.current = getPos(e);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = p.penColor || '#111827';
    ctx.lineWidth = p.penWidth ?? 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPosRef.current = pos;
    setHasSignature(true);
  };

  const stopDraw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    isDrawingRef.current = false;
    const canvas = canvasRef.current!;
    onChange(canvas.toDataURL('image/png'));
  };

  const clear = () => {
    if (printMode) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onChange(null);
  };

  const required = p.required !== false;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, height: '100%', width: '100%' }}>
      <div style={{
        color: p.labelColor || '#374151',
        fontSize: p.labelFontSize || 14,
        fontWeight: 500,
      }}>
        {p.label || 'חתימה דיגיטלית'}
        {required && <span style={{ color: p.requiredColor || '#ef4444', marginRight: 4 }}>*</span>}
      </div>
      <div style={{ position: 'relative', width: '100%', height: canvasH }}>
        <canvas
          ref={canvasRef}
          width={Math.max(200, Math.round(width))}
          height={Math.max(80, Math.round(canvasH))}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
          style={{
            width: '100%',
            height: '100%',
            background: p.canvasBgColor || '#ffffff',
            border: `${p.borderWidth ?? 1}px solid ${p.borderColor || '#d1d5db'}`,
            borderRadius: p.borderRadius ?? 8,
            cursor: printMode ? 'default' : 'crosshair',
            touchAction: 'none',
            display: 'block',
          }}
        />
        {!hasSignature && !printMode && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            fontSize: 14,
            pointerEvents: 'none',
          }}>
            ✍️ {p.placeholderText || 'חתום כאן...'}
          </div>
        )}
      </div>
      {!printMode && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', minHeight: clearHeight }}>
          <button
            type="button"
            onClick={clear}
            disabled={!hasSignature}
            style={{
              fontSize: 12,
              color: hasSignature ? (p.clearButtonColor || '#6b7280') : '#d1d5db',
              background: 'transparent',
              border: 'none',
              padding: '4px 8px',
              cursor: hasSignature ? 'pointer' : 'default',
              textDecoration: 'underline',
            }}
          >
            {p.clearButtonText || 'נקה'}
          </button>
        </div>
      )}
    </div>
  );
};

export const NewBlocksRenderer: React.FC<{
  tool: PublicToolData & { blocks?: BuilderBlock[]; pages?: ToolPageDef[]; pageSettings?: PageSettings };
  primary: string;
  submitted: boolean;
  onSuccess: (ambassador?: AmbassadorResult | null) => void;
  /** Ambassador-signup result, when this form is linked to a campaign. */
  ambassador?: AmbassadorResult | null;
}> = ({ tool, primary, submitted, onSuccess, ambassador }) => {
  const deviceMode = useDeviceMode();
  // Drop blocks the designer hid for this viewport. Filtering once at the
  // top means downstream calculations (canvas height, signature gating,
  // form-field discovery) all see only the visible set. When the per-device-
  // visibility flag is off, no filtering happens — every block renders in
  // every viewport, matching the original behavior.
  //
  // Memoized so the reference stays stable across renders — a bare `.filter()`
  // returns a fresh array each time, which would invalidate any downstream
  // useMemo/useEffect that takes `blocks` as a dep (same trap that caused
  // React error #185 in PublicCampaign).
  const deviceBlocks: BuilderBlock[] = useMemo(() => {
    const raw: BuilderBlock[] = tool.blocks || [];
    if (!featureFlags.perDeviceVisibility) return raw;
    return raw.filter(b => isBlockVisibleInDevice(b, deviceMode));
  }, [tool.blocks, deviceMode]);
  const { values: prefill, printMode } = React.useMemo(getUrlPrefill, []);
  const [formValues, setFormValues] = useState<Record<string, any>>(prefill || {});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Success dialog — opens the moment the submission lands and stays until
  // the submitter dismisses it. The form block underneath keeps its own
  // "נשלח בהצלחה" state, so closing the dialog doesn't offer a second submit.
  const [successOpen, setSuccessOpen] = useState(false);
  const [pdfModal, setPdfModal] = useState<{ url: string; title: string } | null>(null);
  useEffect(() => { if (submitted) setSuccessOpen(true); }, [submitted]);
  useEffect(() => {
    if (!pdfModal) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPdfModal(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [pdfModal]);

  // Multi-page is opt-in; legacy tools continue to render every block.
  const multiPageActive = isMultiPage(tool.pages);
  const pagesSorted = useMemo(
    () => multiPageActive ? sortedPages(tool.pages!) : [],
    [multiPageActive, tool.pages],
  );
  const [currentPageId, setCurrentPageId] = useState<string>(
    multiPageActive ? pagesSorted[0]?.id : '',
  );
  const blocks = useMemo(
    () => multiPageActive
      // Background blocks are page-global (one backdrop for the whole wizard)
      // — same rule as the builder canvas.
      ? deviceBlocks.filter(b => b.type === 'background' || resolveBlockPageId(b.pageId, tool.pages!) === currentPageId)
      : deviceBlocks,
    [deviceBlocks, multiPageActive, tool.pages, currentPageId],
  );
  const isLastPage = !multiPageActive || currentPageId === pagesSorted[pagesSorted.length - 1]?.id;
  const pageCfg = resolvePageSettings(tool.pageSettings);

  const canvasWidth = deviceMode === 'desktop' ? 800 : 390;
  const dynBlockRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [blockGrowth, setBlockGrowth] = useState<Record<string, number>>({});
  // Snapped top per heading/text block: cross-browser field-anchor correction
  // (declared here because the canvas-height pass below reads it).
  const [snapTops, setSnapTops] = useState<Record<string, number>>({});
  // Blocks whose rendered height can massively exceed the authored box: a
  // 20+-field form stacking on a narrow phone, or multi-line heading/text
  // (real newlines render since 2c5c82c8). Without reflow the overflow SITS
  // ON TOP of every block authored below it — visitors saw a heading block
  // swallow the clicks of the radio group underneath. Grown blocks push
  // everything below them down by their overflow.
  //
  // Ordinary pages must still keep the exact editor coordinates, so growth
  // under 24px (browser font/metric drift) is ignored — only real overflow
  // (the ambassador share panel, stacked fields, long text) reflows.
  const grownBlocks = useMemo(() => blocks.filter(b => b.type === 'form' || b.type === 'heading' || b.type === 'text'), [blocks]);
  // Sibling reflow exists for ONE runtime state the builder canvas cannot
  // show: after an ambassador signup the share panel makes the form far
  // taller than its authored box. Everything else renders at the exact
  // authored coordinates — the builder canvas draws blocks over the
  // overflowing form (overflow is visible while editing), orgs align section
  // headings against the VISIBLE fields, and the public page must mirror
  // that 1:1. Growth still extends the document height so nothing is cut.
  const reflowActive = submitted && !!ambassador;

  const measureBlock = useCallback((blockId: string) => {
    const el = dynBlockRefs.current[blockId];
    const block = grownBlocks.find(b => b.id === blockId);
    if (!el || !block) return;
    const layout = deviceMode === 'desktop' ? block.desktopLayout : block.mobileLayout;
    if (!layout) return;
    const raw = el.offsetHeight - layout.height;
    // Round so ResizeObserver cannot oscillate on sub-pixel changes.
    const delta = raw < 24 ? 0 : Math.round(raw / 4) * 4;
    setBlockGrowth(prev => prev[blockId] === delta ? prev : { ...prev, [blockId]: delta });
  }, [grownBlocks, deviceMode]);

  const getReflowOffset = useCallback((targetY: number, excludeBlockId?: string): number => {
    if (!reflowActive) return 0;
    let offset = 0;
    for (const b of grownBlocks) {
      if (b.id === excludeBlockId) continue;
      // Only FORM growth pushes siblings. Section headings are deliberately
      // authored over the form's span, aligned to its field rows — if a grown
      // heading/text (multi-line since 2c5c82c8) pushed them too, they would
      // drift off the rows they label while the fields stay put. Overflowing
      // heading/text is visual-only (and pointer-transparent), exactly like
      // the builder canvas shows it; its height still counts toward the
      // document height below.
      if (b.type !== 'form') continue;
      const layout = deviceMode === 'desktop' ? b.desktopLayout : b.mobileLayout;
      if (!layout || layout.y + layout.height > targetY) continue;
      offset += blockGrowth[b.id] || 0;
    }
    return offset;
  }, [grownBlocks, deviceMode, blockGrowth, reflowActive]);

  // Calculate canvas height from visible block layouts. Background blocks are
  // skipped because they stretch to the canvas instead of defining its height.
  let canvasHeight = 600;
  for (const b of blocks) {
    if (b.type === 'background') continue;
    const layout = deviceMode === 'desktop' ? b.desktopLayout : b.mobileLayout;
    if (layout) {
      const topOffset = getReflowOffset(layout.y, b.id);
      // A block may render taller than its authored box on a narrow phone
      // (labels wrap, fields stack, long text lines break). Include that
      // overflow in the document height so iOS Safari has real scrollable
      // space.
      const ownGrowth = blockGrowth[b.id] || 0;
      const y = snapTops[b.id] ?? layout.y;
      canvasHeight = Math.max(canvasHeight, y + topOffset + layout.height + ownGrowth + 20);
    }
  }

  // Track container width to scale the entire canvas uniformly. The public
  // page becomes a literal zoom of the editor preview — same proportions at
  // any viewport width, just like PublicCampaign does.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(canvasWidth);
  useEffect(() => {
    const update = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    };
    update();
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => { window.removeEventListener('resize', update); ro.disconnect(); };
  }, []);
  const ratio = containerWidth / canvasWidth;

  // ── Cross-browser anchor snap for section headings ───────────────────────
  // A heading laid over the form's span is aligned, in the EDITOR, against
  // the field row it precedes. Other engines wrap the long field labels a
  // little differently (iOS Safari vs desktop Chrome), so by mid-form the
  // fields sit tens of px away from where the org's browser drew them — and a
  // pixel-anchored heading ends up overlapping the wrong row. Snap: find the
  // field whose top sits just below the heading (its editor-time neighbor)
  // and pin the heading the same visual gap above that field's RENDERED top.
  // In the org's own browser the measured layout matches what they authored,
  // so the snap resolves to the exact authored position — zero deviation.
  const computeSnaps = useCallback(() => {
    const next: Record<string, number> = {};
    for (const form of blocks) {
      if (form.type !== 'form') continue;
      const fl = deviceMode === 'desktop' ? form.desktopLayout : form.mobileLayout;
      const el = dynBlockRefs.current[form.id];
      if (!fl || !el) continue;
      const wrapRect = el.getBoundingClientRect();
      if (!wrapRect.width) continue;
      const scale = wrapRect.width / fl.width || 1;
      const fieldTops: number[] = [];
      el.querySelectorAll<HTMLElement>('[data-f]').forEach(fe => {
        fieldTops.push(fl.y + (fe.getBoundingClientRect().top - wrapRect.top) / scale);
      });
      fieldTops.sort((a, b) => a - b);
      const formRenderedH = wrapRect.height / scale;
      for (const b of blocks) {
        if (b.type !== 'heading' && b.type !== 'text') continue;
        const bl = deviceMode === 'desktop' ? b.desktopLayout : b.mobileLayout;
        if (!bl) continue;
        const xOverlap = bl.x < fl.x + fl.width && fl.x < bl.x + bl.width;
        const inSpan = bl.y > fl.y && bl.y < fl.y + Math.max(fl.height, formRenderedH);
        if (!xOverlap || !inSpan) continue;
        const hEl = dynBlockRefs.current[b.id];
        const renderedH = hEl ? hEl.getBoundingClientRect().height / scale : bl.height;
        // Editor-time neighbor: the first field starting at/under the heading
        // top. Cross-engine drift is almost always downward (more wrapping →
        // fields sink), so only a small upward tolerance — a wide one grabs
        // the PREVIOUS field when it hugs the heading from above.
        const neighborTop = fieldTops.find(t => t >= bl.y - 40);
        if (neighborTop === undefined) continue;
        const gapEst = neighborTop - (bl.y + renderedH);
        // Long forms accumulate a lot of cross-engine drift; only bail when
        // the heading was clearly authored far away from any field on purpose.
        if (gapEst < -300 || gapEst > 600) continue;
        // Keep the authored gap when sane; clamp so drift can't reopen an
        // overlap or a hole. gap within [8,60] → newTop === authored y.
        const desiredGap = Math.min(60, Math.max(8, gapEst));
        const newTop = Math.round(neighborTop - desiredGap - renderedH);
        if (Math.abs(newTop - bl.y) > 2) next[b.id] = newTop;
      }
    }
    setSnapTops(prev => {
      const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
      for (const k of keys) {
        if (Math.abs((prev[k] ?? -99999) - (next[k] ?? -99999)) > 2) return next;
      }
      return prev;
    });
  }, [blocks, deviceMode]);

  useLayoutEffect(() => {
    for (const b of grownBlocks) measureBlock(b.id);
    computeSnaps();
  }, [grownBlocks, submitted, ambassador, measureBlock, computeSnaps]);

  // Anything that changed a block's rendered height (late webfonts, viewport
  // rotation, dynamic field content) moved the field rows — re-anchor.
  useEffect(() => { computeSnaps(); }, [blockGrowth, containerWidth, computeSnaps]);
  useEffect(() => {
    let alive = true;
    (document as any).fonts?.ready?.then(() => { if (alive) computeSnaps(); });
    return () => { alive = false; };
  }, [computeSnaps]);

  useEffect(() => {
    const observers: ResizeObserver[] = [];
    for (const b of grownBlocks) {
      const el = dynBlockRefs.current[b.id];
      if (!el) continue;
      const ro = new ResizeObserver(() => { measureBlock(b.id); computeSnaps(); });
      ro.observe(el);
      observers.push(ro);
    }
    return () => observers.forEach(ro => ro.disconnect());
  }, [grownBlocks, submitted, ambassador, measureBlock, computeSnaps]);

  // Block IDs of signature blocks marked as required (default true).
  // Used to gate the form's submit button until each one has been signed.
  const requiredSignatureBlockIds = blocks
    .filter(b => b.type === 'signature' && (b.props?.required !== false))
    .map(b => b.id);
  const allSignaturesSigned = requiredSignatureBlockIds.every(
    id => typeof formValues[id] === 'string' && formValues[id].length > 0,
  );

  // Validate required form fields on current page before allowing navigation
  const validateCurrentPage = useCallback((): boolean => {
    if (!multiPageActive) return true;
    // Find form blocks on current page
    const formBlocks = blocks.filter(b => b.type === 'form');
    for (const fb of formBlocks) {
      const fields = fb.props?.formFields || [];
      for (const field of fields) {
        if (field.type === 'section') continue;
        if (field.required) {
          const val = formValues[field.id || field.label];
          if (!val || (typeof val === 'string' && !val.trim())) {
            setValidationError(`יש למלא את השדה "${field.label}" לפני המעבר`);
            return false;
          }
        }
      }
    }
    // Required signature blocks must be signed before leaving their page —
    // the final submit can't reach back into an earlier page to collect one.
    for (const sig of blocks.filter(b => b.type === 'signature' && b.props?.required !== false)) {
      const v = formValues[sig.id];
      if (typeof v !== 'string' || v.length === 0) {
        setValidationError('יש להוסיף חתימה דיגיטלית לפני המעבר לעמוד הבא');
        return false;
      }
    }
    setValidationError(null);
    return true;
  }, [blocks, formValues, multiPageActive]);

  const handlePageNavigate = useCallback((pageId: string) => {
    // Going backwards is always allowed
    const targetIdx = pagesSorted.findIndex(p => p.id === pageId);
    const currentIdx = pagesSorted.findIndex(p => p.id === currentPageId);
    if (targetIdx < currentIdx) {
      setCurrentPageId(pageId);
      setValidationError(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // Going forward requires validation
    if (validateCurrentPage()) {
      setCurrentPageId(pageId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pagesSorted, currentPageId, validateCurrentPage]);

  const handleFormSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (printMode) return;
    setFormError(null);
    // Trim string values: a field with just spaces is effectively empty but
    // bypasses the native `required` check. Reject blank-after-trim required
    // fields with a friendly message, and enforce numeric min/max range.
    // checkbox / radio / checkbox-group are custom-styled components with no
    // native <input required>, so the browser can't enforce them — an
    // untouched field is simply absent from formValues (or false / []).
    // This loop is their only client-side gate.
    const cleaned: Record<string, any> = {};
    let blankRequired = '';
    let blankIsCheckbox = false;
    for (const [k, v] of Object.entries(formValues)) {
      cleaned[k] = typeof v === 'string' ? v.trim() : v;
    }
    const fields = (tool.blocks || []).flatMap((b: any) => (b?.props?.formFields || []) as any[]);
    for (const f of fields) {
      if (f.type === 'section') continue;
      if (f.required) {
        const v = cleaned[f.id];
        const empty = v === undefined || v === null || v === '' || v === false
          || (Array.isArray(v) && v.length === 0);
        if (empty) {
          blankRequired = String(f.label || f.placeholder || 'שדה חובה').trim();
          blankIsCheckbox = f.type === 'checkbox' || f.type === 'checkbox-group';
          break;
        }
      }
      if (f.type === 'number' && cleaned[f.id] !== undefined && cleaned[f.id] !== '') {
        const n = Number(cleaned[f.id]);
        if (Number.isFinite(n)) {
          if (typeof f.minValue === 'number' && n < f.minValue) {
            setFormError(`"${f.label}" חייב להיות לפחות ${f.minValue}`);
            return;
          }
          if (typeof f.maxValue === 'number' && n > f.maxValue) {
            setFormError(`"${f.label}" חייב להיות לכל היותר ${f.maxValue}`);
            return;
          }
        }
      }
    }
    if (blankRequired) {
      // Health-declaration checkboxes carry paragraph-long labels — keep the
      // error readable.
      const shortLabel = blankRequired.length > 60 ? `${blankRequired.slice(0, 60)}…` : blankRequired;
      setFormError(blankIsCheckbox
        ? `יש לסמן את שדה החובה "${shortLabel}"`
        : `לא ניתן להשאיר את "${shortLabel}" ריק`);
      return;
    }
    if (!allSignaturesSigned) {
      setFormError('יש להוסיף חתימה דיגיטלית לפני שליחה');
      return;
    }
    try {
      setFormSubmitting(true);
      const res = await axios.post(`${API_BASE}/public/tools/${tool.slug}/submit`, { data: cleaned, mc: getMcParam() });
      // Reset form state after a successful submit so the next submission
      // (e.g. on a kiosk page reused for multiple visitors) starts clean.
      // Notably wipes signatures — previously they lingered as dataURLs.
      setFormValues({});
      onSuccess(res.data?.ambassador || null);
      // Ambassador signups never auto-redirect — the personal share link IS
      // the result of the form, and bouncing the page away would lose it.
      if (tool.redirectUrl && !res.data?.ambassador) {
        setTimeout(() => { window.location.href = tool.redirectUrl; }, 1500);
      }
    } catch (err: any) {
      setFormError(err?.response?.data?.error || 'שגיאה בשליחה');
    } finally {
      setFormSubmitting(false);
    }
  };

  const renderBlock = (block: BuilderBlock) => {
    // Background blocks always fill the entire canvas — same behavior as the
    // editor and PublicCampaign. Their stored layout is ignored so adding new
    // content (which grows the canvas) never leaves a strip below the background.
    const layout = block.type === 'background'
      ? { x: 0, y: 0, width: canvasWidth, height: canvasHeight }
      : (deviceMode === 'desktop' ? block.desktopLayout : block.mobileLayout);
    if (!layout) return null;

    const s = deviceMode === 'mobile' ? { ...block.style, ...block.mobileStyle } : (block.style || {});
    const p = deviceMode === 'mobile' ? { ...block.props, ...block.mobileProps } : (block.props || {});

    // Tiered z-index — keeps visual blocks (image/gallery/video/container)
    // BEHIND content blocks (text/heading/button/form/etc.) so a misplaced
    // image never buries the call-to-action. 0 = background, 1 = media,
    // 2 = content. Mirrors getBlockStackTier in the builder.
    const mediaTypes = new Set(['image', 'gallery', 'video', 'container']);
    // Explicit per-block stacking override (bring-to-front / send-behind from
    // the builder) wins over the type tier — lets a logo image sit above the
    // hero. Background stays at the back regardless.
    const zOverride = (s as any)?.zLayer;
    const zIndex = block.type === 'background' ? 0
      : (zOverride != null ? zOverride : (mediaTypes.has(block.type) ? 1 : 2));

    // Blocks whose content can grow taller than the authored height
    // (long text, wrapping social icons, responsive form fields, …).
    // These must stay auto-height so nothing gets clipped. Gallery is
    // NOT in this list — it honors the authored height so images scale
    // proportionally to what the designer laid out (matches the builder).
    // 'chart' intentionally excluded — Recharts needs an explicit fixed height to render
    const autoHeightTypes = ['form', 'text', 'heading', 'social', 'progress', 'countdown', 'button', 'donation-tiers', 'payment-button', 'recent-donors', 'recent-submissions', 'student-registration', 'trainee-registration', 'share'];
    const isAuto = autoHeightTypes.includes(block.type);

    // The whole canvas renders at its authored pixel size and is uniformly
    // scaled by transform: scale(ratio) on the parent — so each block uses
    // the exact pixel coordinates from the editor.
    const anchoredY = snapTops[block.id] ?? layout.y;
    const wrapStyle: React.CSSProperties = {
      position: 'absolute',
      left: layout.x,
      top: anchoredY + getReflowOffset(layout.y, block.id),
      width: layout.width,
      height: isAuto ? 'auto' : layout.height,
      minHeight: isAuto ? layout.height : undefined,
      overflow: isAuto ? 'visible' : 'hidden',
      borderRadius: s.borderRadius ? `${s.borderRadius}px` : undefined,
      zIndex,
    };

    const baseStyle: React.CSSProperties = {
      width: '100%', height: '100%',
      backgroundColor: s.bgTransparent ? 'transparent' : (s.bgColor || undefined),
      backgroundImage: s.bgTransparent ? 'none' : (s.bgGradient || undefined),
      color: s.textColor || undefined,
      fontSize: s.fontSize ? `${s.fontSize}px` : undefined,
      fontFamily: s.fontFamily || undefined,
      fontWeight: s.fontWeight || undefined,
      textAlign: s.textAlign || undefined,
      padding: s.padding !== undefined ? `${s.padding}px` : undefined,
      opacity: s.opacity !== undefined ? s.opacity / 100 : undefined,
      // Mirror borderRadius onto the inner (colored) box. Form blocks need
      // overflow:visible on the wrapper so <select> dropdowns aren't clipped,
      // which means wrapper borderRadius is invisible. Putting it on baseStyle
      // rounds the visible card regardless of wrapper overflow.
      borderRadius: s.borderRadius ? `${s.borderRadius}px` : undefined,
    };

    switch (block.type) {
      case 'hero': {
        const slideshowImages: string[] = p.slideshowImages || [];
        const hasSlideshow = slideshowImages.length > 1;
        const [slideIdx, setSlideIdx] = React.useState(0);
        React.useEffect(() => {
          if (!hasSlideshow) return;
          const speed = (p.slideshowSpeed || 5) * 1000;
          const timer = setInterval(() => setSlideIdx(prev => (prev + 1) % slideshowImages.length), speed);
          return () => clearInterval(timer);
        }, [hasSlideshow, slideshowImages.length, p.slideshowSpeed]);

        const currentImage = hasSlideshow ? slideshowImages[slideIdx] : p.imageUrl;
        // Gradient is a fallback for when there is no image — the builder
        // treats it the same way. Never overlay it on top of a photo; that
        // recolors the image and breaks the designer's intent.
        const heroBg = currentImage ? `url(${currentImage})` : (p.bgGradient || s.bgGradient || undefined);
        const imageLayerOpacity = heroImageOpacity(p);
        // Image brightness is a filter on the image layer only; title/subtitle
        // carry their own colour + brightness (shared with the builder).
        const imageFilter = heroImageFilter(p);
        const heroText = heroTextStyles(p, s);
        // Block-level opacity applies to bg layers ONLY — text stays fully legible.
        const bgOpacity = s.opacity !== undefined ? s.opacity / 100 : 1;

        return (
          <div key={block.id} style={{ ...wrapStyle, overflow: 'hidden' }}>
            {/* Color layer — rendered first so image can overlay with its own opacity */}
            {!s.bgTransparent && (
              <div style={{ position: 'absolute', inset: 0, backgroundColor: s.bgColor || '#1e40af', opacity: bgOpacity }} />
            )}
            {/* Background images with fade */}
            {hasSlideshow ? slideshowImages.map((img, i) => (
              <div key={i} style={{
                position: 'absolute', inset: 0,
                backgroundImage: `url(${img})`, backgroundSize: 'cover', backgroundPosition: 'center',
                opacity: (i === slideIdx ? imageLayerOpacity : 0) * bgOpacity,
                filter: imageFilter,
                transition: 'opacity 1s ease-in-out',
              }} />
            )) : heroBg ? (
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: heroBackgroundImage(s, heroBg, currentImage),
                backgroundSize: 'cover', backgroundPosition: 'center',
                opacity: imageLayerOpacity * bgOpacity,
                filter: imageFilter,
              }} />
            ) : null}
            {/* Content */}
            <div style={{
              position: 'relative', zIndex: 1, width: '100%', height: '100%',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
              color: p.textColor || s.textColor || '#fff',
              padding: s.padding !== undefined ? `${s.padding}px` : '32px',
            }}>
              <h1 style={heroText.title}>{block.content || ''}</h1>
              {p.subtitle && <p style={heroText.subtitle}>{p.subtitle}</p>}
            </div>
          </div>
        );
      }
      case 'heading': {
        // Rich-text blocks store their line breaks / formatting as inline HTML
        // (the builder's InlineRichTextEditor commits `<br>`, `<b>`, spans…).
        // Mirror PublicCampaign: render that HTML — otherwise visitors see the
        // literal tags. Plain content still gets literal-<br> normalisation in
        // case it was authored outside the editor (AI / API / paste).
        const isRichH = !!block.props?.isRichText;
        const headingInner = { width: '100%', textAlign: s.textAlign || undefined, fontSize: `${s.fontSize || 28}px`, fontWeight: s.fontWeight || 'bold', color: s.textColor || '#111827' };
        return (
          <div key={block.id} ref={el => { dynBlockRefs.current[block.id] = el; }} style={{ ...wrapStyle, pointerEvents: 'none' }}>
            <div style={{ ...baseStyle, display: 'flex', alignItems: 'center' }}>
              {isRichH
                ? <div style={headingInner} dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(block.content || '') }} />
                : <div style={{ ...headingInner, whiteSpace: 'pre-wrap' }}>{literalBrToNewline(block.content || '')}</div>}
            </div>
          </div>
        );
      }
      case 'text': {
        const isRichT = !!block.props?.isRichText;
        const textStyle = { fontSize: `${s.fontSize || 16}px`, color: s.textColor || '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' as const };
        return (
          <div key={block.id} ref={el => { dynBlockRefs.current[block.id] = el; }} style={{ ...wrapStyle, pointerEvents: 'none' }}>
            <div style={{ ...baseStyle }}>
              {isRichT
                ? <p style={textStyle} dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(block.content || '') }} />
                : <p style={textStyle}>{literalBrToNewline(block.content || '')}</p>}
            </div>
          </div>
        );
      }
      case 'image': {
        const imgPosX = typeof p.posX === 'number' ? p.posX : 50;
        const imgPosY = typeof p.posY === 'number' ? p.posY : 50;
        const imgScale = typeof p.posScale === 'number' ? p.posScale : 1;
        return (
          <div key={block.id} style={{
            ...wrapStyle, overflow: 'hidden',
            // Honor the "ריווח פנימי" (padding) slider — mirror the editor,
            // which insets the image via baseStyle. border-box keeps the frame
            // at the authored size and pushes the image inward.
            padding: s.padding !== undefined ? `${s.padding}px` : undefined,
            boxSizing: 'border-box',
          }}>
            {p.url ? (
              <PannedImage
                url={p.url}
                alt={p.alt || ''}
                fit={p.objectFit || 'cover'}
                posX={imgPosX}
                posY={imgPosY}
                posScale={imgScale}
                radius={s.borderRadius ? `${s.borderRadius}px` : undefined}
              />
            ) : (
              <div style={{ width: '100%', height: '100%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                תמונה
              </div>
            )}
          </div>
        );
      }
      case 'button':
        return (
          <div key={block.id} style={wrapStyle}>
            <div style={{ ...baseStyle, display: 'flex', justifyContent: p.align || 'center', alignItems: 'center' }}>
              <a
                href={p.href || '#'}
                onClick={p.openPdfModal ? event => {
                  event.preventDefault();
                  setPdfModal({ url: p.href || '', title: block.content || 'מסמך PDF' });
                } : undefined}
                target={p.openInNewTab ? '_blank' : undefined}
                rel={p.openInNewTab ? 'noopener noreferrer' : undefined}
                style={{
                  backgroundColor: s.bgColor || primary, color: s.textColor || '#fff',
                  borderRadius: s.borderRadius !== undefined ? `${s.borderRadius}px` : '8px',
                  fontSize: `${s.fontSize || 16}px`, fontWeight: 'bold',
                  padding: p.size === 'lg' ? '14px 32px' : p.size === 'sm' ? '6px 16px' : '10px 24px',
                  textDecoration: 'none', display: 'inline-block',
                }}
              >
                {block.content || 'כפתור'}
              </a>
            </div>
          </div>
        );
      case 'share': {
        // Shares the live URL, so an ambassador's ?ref= survives into the
        // link they pass on. Visuals are shared 1:1 with the builder canvas
        // and PublicCampaign via ShareBlock.
        const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
        return (
          <div key={block.id} style={wrapStyle}>
            <div style={{ ...baseStyle, height: 'auto' }}>
              <ShareBlock
                props={p}
                shareUrl={shareUrl}
                shareTitle={tool.title || tool.name || ''}
                fontFamily={s.fontFamily}
              />
            </div>
          </div>
        );
      }
      case 'form': {
        const formFields = p.formFields || [];
        if (submitted) {
          return (
            <div
              key={block.id}
              data-block-id={block.id}
              ref={el => { dynBlockRefs.current[block.id] = el; }}
              style={wrapStyle}
            >
              <div style={{ ...baseStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                <h3 style={{ fontSize: 20, fontWeight: 800, color: '#111827', marginBottom: 8 }}>נשלח בהצלחה!</h3>
                <p style={{ color: '#6b7280' }}>{tool.successMessage || 'תודה! הטופס התקבל.'}</p>
                {ambassador && (
                  <div style={{ width: '100%', maxWidth: 460 }}>
                    <AmbassadorSharePanel amb={ambassador} primary={primary} />
                  </div>
                )}
              </div>
            </div>
          );
        }
        return (
          <div
            key={block.id}
            data-block-id={block.id}
            ref={el => { dynBlockRefs.current[block.id] = el; }}
            style={wrapStyle}
          >
            <div style={{ ...baseStyle }}>
              <form
                onSubmit={isLastPage ? handleFormSubmit : e => {
                  e.preventDefault();
                  if (validateCurrentPage()) {
                    const currentIdx = pagesSorted.findIndex(page => page.id === currentPageId);
                    const nextPage = pagesSorted[currentIdx + 1];
                    if (nextPage) handlePageNavigate(nextPage.id);
                  }
                }}
                data-multipage-form
                style={{ display: 'flex', flexWrap: 'wrap', gap: p.fieldGap ?? 12 }}
                dir="rtl"
              >
                {formFields.map((field: any) => (
                  <div
                    key={field.id}
                    data-f={field.id}
                    style={deviceMode === 'mobile' && field.mobileWidth === 'full'
                      ? { ...fsFieldWrap(field), flex: undefined, width: '100%' }
                      : fsFieldWrap(field)}
                  >
                    {!field.hideLabel && field.type !== 'section' && (
                      <label style={fsLabel(p, field)}>
                        {field.label}{field.required && <span style={fsRequired(p)}>*</span>}
                      </label>
                    )}
                    {field.type === 'section' ? (
                      <div style={{ width: '100%', fontSize: (field.fontSize || (p.labelFontSize || 14) + 6), fontWeight: 800, color: p.labelColor || '#111827', padding: '8px 0 2px', whiteSpace: 'pre-wrap' }}>
                        {field.label}
                      </div>
                    ) : field.type === 'textarea' ? (
                      <textarea
                        placeholder={fsPlaceholder(field)} rows={3} required={field.required}
                        value={formValues[field.id] || ''}
                        onChange={e => setFormValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                        style={fsTextarea(p, field)}
                      />
                    ) : field.type === 'select' ? (
                      <select
                        value={formValues[field.id] || ''} required={field.required}
                        onChange={e => setFormValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                        style={fsInput(p, field)}
                      >
                        <option value="">{fsPlaceholder(field) || 'בחר...'}</option>
                        {(field.options || []).map((opt: string, i: number) => <option key={i} value={opt}>{opt}</option>)}
                      </select>
                    ) : field.type === 'checkbox' ? (
                      <CheckboxOption
                        checked={!!formValues[field.id]}
                        onToggle={() => setFormValues(prev => ({ ...prev, [field.id]: !prev[field.id] }))}
                        label={fsPlaceholder(field) || field.label}
                        required={!!field.required && !!field.hideLabel}
                        variant={field.checkboxVariant || p.checkboxVariant || 'square'}
                        color={field.checkboxColor || p.checkboxColor || '#2563eb'}
                        size={field.checkboxSize || p.checkboxSize || 18}
                        textColor={p.checkboxLabelColor || '#374151'}
                        textSize={field.fontSize || p.inputFontSize || 14}
                      />
                    ) : field.type === 'checkbox-group' ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: (field.optionsLayout || 'column') === 'row' ? 'row' : 'column',
                        flexWrap: 'wrap',
                        gap: field.optionsLayout === 'row' ? 8 : 6,
                      }}>
                        {(field.options || []).map((opt: string, oi: number) => {
                          const selected: string[] = Array.isArray(formValues[field.id]) ? formValues[field.id] : [];
                          const isChecked = selected.includes(opt);
                          return (
                            <CheckboxOption
                              key={oi} checked={isChecked}
                              onToggle={() => {
                                const next = isChecked ? selected.filter(x => x !== opt) : [...selected, opt];
                                setFormValues(prev => ({ ...prev, [field.id]: next }));
                              }}
                              label={opt}
                              variant={field.checkboxVariant || p.checkboxVariant || 'square'}
                              color={field.checkboxColor || p.checkboxColor || '#2563eb'}
                              size={field.checkboxSize || p.checkboxSize || 18}
                              textColor={p.checkboxLabelColor || '#374151'}
                              textSize={field.fontSize || p.inputFontSize || 14}
                            />
                          );
                        })}
                      </div>
                    ) : field.type === 'radio' ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: (field.optionsLayout || 'column') === 'row' ? 'row' : 'column',
                        flexWrap: 'wrap',
                        gap: field.optionsLayout === 'row' ? 8 : 6,
                      }}>
                        {(field.options || []).map((opt: string, oi: number) => (
                          <CheckboxOption
                            key={oi} isRadio
                            checked={formValues[field.id] === opt}
                            onToggle={() => setFormValues(prev => ({ ...prev, [field.id]: opt }))}
                            label={opt}
                            variant={field.checkboxVariant || p.checkboxVariant || 'square'}
                            color={field.checkboxColor || p.checkboxColor || '#2563eb'}
                            size={field.checkboxSize || p.checkboxSize || 18}
                            textColor={p.checkboxLabelColor || '#374151'}
                            textSize={field.fontSize || p.inputFontSize || 14}
                          />
                        ))}
                      </div>
                    ) : field.type === 'date' ? (
                      <div
                        data-date-input-shell="true"
                        style={{
                          ...fsInput(p, field),
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          direction: 'ltr',
                          overflow: 'hidden',
                        }}
                      >
                        <span style={{
                          color: formValues[field.id] ? (field.textColor || p.inputTextColor || '#111827') : '#9ca3af',
                          pointerEvents: 'none',
                          whiteSpace: 'nowrap',
                        }}>
                          {formValues[field.id]
                            ? String(formValues[field.id]).split('-').reverse().join('.')
                            : (fsPlaceholder(field) || 'dd.mm.yyyy')}
                        </span>
                        {/* The invisible native control has no calendar icon of
                            its own — this one tells the visitor the blank opens
                            a picker. */}
                        <span aria-hidden style={{ marginInlineStart: 'auto', paddingInlineStart: 8, opacity: 0.55, pointerEvents: 'none', fontSize: '0.95em' }}>📅</span>
                        <input
                          type="date"
                          aria-label={field.label || 'תאריך'}
                          required={field.required}
                          value={formValues[field.id] || ''}
                          onChange={e => setFormValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                          // The native control is invisible (the shell above
                          // draws the value), so desktop browsers get no
                          // calendar icon to click — without showPicker() a
                          // click anywhere on the blank does nothing at all.
                          // Not supported everywhere / throws without a user
                          // gesture, hence the guard.
                          onClick={e => { try { (e.currentTarget as any).showPicker?.(); } catch { /* unsupported browser */ } }}
                          onKeyDown={e => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            e.preventDefault();
                            try { (e.currentTarget as any).showPicker?.(); } catch { /* unsupported browser */ }
                          }}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            minWidth: 0,
                            maxWidth: '100%',
                            margin: 0,
                            padding: 0,
                            border: 0,
                            opacity: 0,
                            cursor: 'pointer',
                          }}
                        />
                      </div>
                    ) : (
                      <input
                        type={field.type === 'tel' ? 'tel' : field.type === 'email' ? 'email' : field.type === 'number' ? 'number' : 'text'}
                        inputMode={field.type === 'number' ? 'numeric' : undefined}
                        autoComplete={field.type === 'address' ? 'street-address' : undefined}
                        placeholder={fsPlaceholder(field)}
                        required={field.required}
                        min={field.type === 'number' && typeof field.minValue === 'number' ? field.minValue : undefined}
                        max={field.type === 'number' && typeof field.maxValue === 'number' ? field.maxValue : undefined}
                        value={formValues[field.id] || ''}
                        onChange={e => setFormValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                        style={fsInput(p, field)}
                      />
                    )}
                  </div>
                ))}
                {formError && <div style={{ width: '100%', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 8, color: '#dc2626', fontSize: 13 }}>{formError}</div>}
                {!printMode && !multiPageActive && (() => {
                  const blockedBySignature = !allSignaturesSigned;
                  const isDisabled = formSubmitting || blockedBySignature;
                  return (
                    <div style={{ ...fsSubmitWrap(p), width: '100%', marginTop: 4 }}>
                      <button
                        type="submit" disabled={isDisabled}
                        style={{
                          ...fsSubmit(p),
                          opacity: isDisabled ? 0.6 : 1,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {p.submitIcon && <span style={{ marginLeft: 6 }}>{p.submitIcon}</span>}
                        {formSubmitting ? 'שולח...' : (p.submitText || 'שלח')}
                      </button>
                      {blockedBySignature && (
                        <div style={{ marginTop: 6, fontSize: 12, color: '#ef4444', textAlign: 'center' }}>
                          ✍️ נדרשת חתימה דיגיטלית לשליחת הטופס
                        </div>
                      )}
                    </div>
                  );
                })()}
              </form>
            </div>
          </div>
        );
      }
      case 'signature': {
        return (
          <div key={block.id} style={wrapStyle}>
            <div style={{ ...baseStyle, padding: s.padding ?? 8 }}>
              <SignatureCanvas
                value={formValues[block.id] || ''}
                onChange={(dataUrl) => setFormValues(prev => {
                  const next = { ...prev };
                  if (dataUrl) next[block.id] = dataUrl;
                  else delete next[block.id];
                  return next;
                })}
                props={p}
                width={layout.width - 2 * (s.padding ?? 8)}
                height={layout.height - 2 * (s.padding ?? 8)}
                printMode={printMode}
              />
            </div>
          </div>
        );
      }
      case 'student-registration': {
        // Public tuition-registration form — self-contained (fetches its own
        // class list + submits a pending registration via the tools namespace).
        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            <StudentRegistrationBlock slug={tool.slug} blockId={block.id} p={p} baseStyle={baseStyle} apiKind="tools" />
          </div>
        );
      }
      case 'trainee-registration': {
        // Public training-group registration form — self-contained (fetches
        // its own group list + submits a pending registration via the tools
        // namespace).
        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            <TraineeRegistrationBlock slug={tool.slug} blockId={block.id} p={p} baseStyle={baseStyle} apiKind="tools" />
          </div>
        );
      }
      case 'countdown': {
        // 'תחילת הקמפיין' mode: for a form linked to a campaign
        // (Tool.ambassadorCampaignId) the public payload carries that
        // campaign's launch time, so an ambassador-recruitment page can show
        // "the campaign starts in …" and drop the block once it's live.
        const cd = resolveCountdown(p, {
          campaignStartDate: (tool as any)?.campaignStartDate,
          campaignEndDate: (tool as any)?.campaignEndDate,
        });
        const countdownTargetMs = cd.target?.getTime() ?? null;
        const [remaining, setRemaining] = React.useState(() => countdownParts(cd.target));
        React.useEffect(() => {
          const update = () => setRemaining(countdownParts(cd.target));
          update();
          const interval = setInterval(update, 1000);
          return () => clearInterval(interval);
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [p.targetDate, p.source, (tool as any)?.campaignStartDate, (tool as any)?.campaignEndDate, countdownTargetMs]);
        if (cd.mode === 'hidden') return null;

        const units = [
          { val: String(remaining.days).padStart(2, '0'), label: 'ימים' },
          { val: String(remaining.hours).padStart(2, '0'), label: 'שעות' },
          { val: String(remaining.mins).padStart(2, '0'), label: 'דקות' },
          { val: String(remaining.secs).padStart(2, '0'), label: 'שניות' },
        ];
        return (
          <div key={block.id} style={wrapStyle}>
            <div style={{ ...baseStyle, backgroundColor: 'transparent', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              {(cd.mode === 'preLaunch' || cd.mode === 'campaignEnd') && (
                <div style={{
                  fontSize: p.preLaunchLabelSize || 14, fontWeight: 700,
                  color: p.preLaunchLabelColor || s.textColor || '#374151',
                  marginBottom: 6,
                }}>
                  {cd.mode === 'campaignEnd' ? CAMPAIGN_END_LABEL : (p.preLaunchLabel || PRE_LAUNCH_LABEL)}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                {units.map(({ val, label }, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      background: s.bgColor || '#2563eb', color: '#fff',
                      borderRadius: s.borderRadius || 10, width: 56, height: 52,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 'bold', fontSize: 22,
                      border: p.borderWidth ? `${p.borderWidth}px solid ${p.borderColor || '#e5e7eb'}` : undefined,
                    }}>{val}</div>
                    {p.showLabels !== false && <span style={{ fontSize: p.labelSize || 11, color: p.labelColor || '#6b7280', fontFamily: p.labelFont || undefined, marginTop: 4 }}>{label}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }
      case 'gallery': {
        const layoutMode = p.layout || 'grid';
        const cols = p.columns || 3;
        const gap = p.gap || 8;
        const fit = p.imageFit || 'cover';
        const images: string[] = p.images || [];
        const colCount = layoutMode === 'row' ? (images.length || 1) : layoutMode === 'column' ? 1 : cols;
        const radius = s.borderRadius ? `${s.borderRadius}px` : '6px';
        // Cells fill the authored block dimensions — matches how the
        // builder renders. object-fit:cover crops images to preserve their
        // ratio inside the cell without distortion.
        if (layoutMode === 'carousel') {
          return (
            <div key={block.id} style={{ ...wrapStyle, overflow: 'hidden' }}>
              <div style={{ ...baseStyle, width: '100%', height: '100%' }}>
                <GalleryCarousel
                  images={images}
                  perView={cols}
                  spans={gallerySpans(p, images.length)}
                  fits={galleryFits(p, images.length)}
                  gap={gap}
                  fit={fit}
                  radius={radius}
                  {...galleryArrowProps(p)}
                  showDots={p.showDots !== false}
                />
              </div>
            </div>
          );
        }
        return (
          <div key={block.id} style={wrapStyle}>
            <div style={{
              ...baseStyle,
              display: 'grid',
              gridTemplateColumns: `repeat(${colCount}, 1fr)`,
              ...(galleryHasSpans(p) ? { gridAutoRows: 'minmax(0, 1fr)' } : {}),
              gap,
            }}>
              {images.length > 0 ? images.map((url, i) => (
                <div key={i} style={{ width: '100%', height: '100%', borderRadius: radius, overflow: 'hidden', background: '#e5e7eb', minHeight: 0, ...galleryCellStyle(p, i, colCount) }}>
                  {url && <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: galleryFitFor(p, i, fit) as any, display: 'block' }} />}
                </div>
              )) : (
                <div style={{ gridColumn: `span ${colCount}`, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>גלריה</div>
              )}
            </div>
          </div>
        );
      }

      case 'video': {
        const videos = videoItemsFromProps(p);
        // Landing pages historically stretched the player over the whole
        // authored box, so an untouched block keeps the 'fill' shape; picking
        // an orientation in the builder switches to a real aspect box.
        const frame = videoFrameStyle(p, s.borderRadius || 0, 'fill');
        if (p.videoMode === 'carousel' && videos.length > 0) {
          return (
            <div key={block.id} style={wrapStyle}>
              <VideoCarousel
                videos={videos}
                radius={s.borderRadius !== undefined ? `${s.borderRadius}px` : '8px'}
                {...galleryArrowProps(p)}
                showDots={p.showDots !== false}
                autoPlay={!!p.carouselAutoPlay}
                speed={p.carouselSpeed || 6}
                frame={videoOrientationOf(p, 'fill') === 'fill' ? undefined : frame}
              />
            </div>
          );
        }
        const embed = parseVideoUrl(p.url, p.startSeconds);
        return (
          <div key={block.id} style={wrapStyle}>
            {embed ? (
              <div style={frame.outer}>
                <div style={frame.inner}>
                  {embed.kind === 'iframe' ? (
                    <iframe src={embed.src} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} allowFullScreen title="video" />
                  ) : (
                    <video src={embed.src} controls style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
                  )}
                </div>
              </div>
            ) : (
              <div style={{ ...baseStyle, background: '#f3f4f6', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', padding: 12, textAlign: 'center', fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>🎬</div>
                {!p.url ? 'אין קישור וידאו' : <>קישור לא מזוהה:<br /><span style={{ fontSize: 11, wordBreak: 'break-all' }}>{p.url}</span></>}
              </div>
            )}
          </div>
        );
      }
      case 'social': {
        const socialNetworks = [
          { key: 'whatsapp', label: 'WhatsApp', color: '#25D366', svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.533 5.845L.057 23.428a.5.5 0 0 0 .515.572l5.758-1.51A11.946 11.946 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.797 9.797 0 0 1-5.003-1.376l-.358-.214-3.718.975.994-3.633-.234-.374A9.799 9.799 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z"/></svg> },
          { key: 'youtube', label: 'YouTube', color: '#FF0000', svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/></svg> },
          { key: 'facebook', label: 'Facebook', color: '#1877F2', svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
          { key: 'instagram', label: 'Instagram', color: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg> },
          { key: 'tiktok', label: 'TikTok', color: '#000000', svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/></svg> },
          { key: 'website', label: 'אתר', color: '#64748b', svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg> },
        ];
        const iconSize = p.iconSize || 40;
        const gap = p.gap || 16;
        const visibleNetworks = socialNetworks.filter(n => p[n.key]?.url);
        const displayNetworks = visibleNetworks.length > 0 ? visibleNetworks : socialNetworks;
        return (
          <div key={block.id} style={wrapStyle}>
            <div style={{ ...baseStyle, display: 'flex', justifyContent: p.align || 'center', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: `${gap}px`, justifyContent: p.align || 'center' }}>
                {displayNetworks.map(net => {
                  const url = p[net.key]?.url;
                  const Wrapper = url ? 'a' : 'span';
                  const linkProps = url ? { href: url, target: '_blank', rel: 'noopener noreferrer' } : {};
                  return (
                    <Wrapper key={net.key} {...linkProps as any} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
                      <span style={{ width: iconSize, height: iconSize, borderRadius: '50%', background: net.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ width: iconSize * 0.55, height: iconSize * 0.55, display: 'flex' }}>{net.svg}</span>
                      </span>
                    </Wrapper>
                  );
                })}
              </div>
            </div>
          </div>
        );
      }
      case 'progress': {
        // Auto-fill toolSlug so the block can fetch live data on the public page
        // without the author having to wire it up manually.
        const progProps = { ...p, toolSlug: p.toolSlug || tool?.slug };
        return (
          <div key={block.id} style={wrapStyle}>
            <DynamicProgress props={progProps} style={s} baseStyle={baseStyle} playEntrance />
          </div>
        );
      }
      case 'recent-submissions': {
        // Auto-fill toolSlug so it defaults to THIS page's own form unless the
        // author picked another. The block self-fetches the latest submissions.
        const rsProps = { ...p, toolSlug: p.toolSlug || tool?.slug };
        return (
          <div key={block.id} style={wrapStyle}>
            <RecentSubmissionsBlock props={rsProps} style={s} baseStyle={baseStyle} locale="he" pageSlug={tool?.slug} />
          </div>
        );
      }
      case 'chart': {
        const chartProps = { ...p, toolSlug: p.toolSlug || tool?.slug };
        return (
          <div key={block.id} style={wrapStyle}>
            <DynamicChart props={chartProps} style={s} baseStyle={baseStyle} />
          </div>
        );
      }
      // background / container — visual layers that live BEHIND every
      // other block on the page. Must NOT fall through to default, otherwise
      // the block type name leaks as visible text.
      case 'background': {
        // Layered background (decorative pattern → photo/gradient → solid
        // colour) computed by the SAME shared helper the builder canvas and
        // PublicCampaign use, so what the designer sees in the editor is what
        // visitors get. This used to be a hand-rolled copy that only knew about
        // photo + gradient, which silently dropped every `bgPattern*` prop —
        // the "צורות" picker had no effect on the live page.
        return (
          <div key={block.id} style={wrapStyle}>
            <div
              style={{
                ...baseStyle,
                boxSizing: 'border-box',
                ...backgroundLayerStyle(s, p),
              }}
            />
          </div>
        );
      }
      case 'container': {
        // Containers deliberately stay on the simple photo/gradient path — the
        // builder's container renderer has no pattern layer, and the design
        // panel only offers the image/shape pickers for `background` blocks.
        const bgImage = p.imageUrl || p.bgImage;
        return (
          <div key={block.id} style={wrapStyle}>
            <div
              style={{
                ...baseStyle,
                boxSizing: 'border-box',
                backgroundImage: s.bgTransparent
                  ? 'none'
                  : bgImage
                    ? `url("${bgImage}")`
                    : (s.bgGradient || undefined),
                backgroundSize: bgImage ? 'cover' : undefined,
                backgroundPosition: bgImage ? 'center' : undefined,
                backgroundRepeat: bgImage ? 'no-repeat' : undefined,
              }}
            />
          </div>
        );
      }
      default:
        // Never render raw block-type names to the visitor — that was
        // showing "background" / "container" on live pages in the past.
        // Prefer authored content; otherwise render nothing.
        if (!block.content) return null;
        return (
          <div key={block.id} style={wrapStyle}>
            <div style={baseStyle}>{block.content}</div>
          </div>
        );
    }
  };

  // Outer wrapper measures the available width. The inner canvas renders
  // blocks at their authored pixel coordinates and is uniformly scaled by a
  // single transform — the public page is a literal zoom of the editor
  // preview at any viewport width (matches PublicCampaign behavior).
  const canvasElement = (
    <div ref={containerRef} style={{
      width: '100%',
      maxWidth: '100%',
      margin: '0 auto',
      position: 'relative',
      height: canvasHeight * ratio,
      background: '#fff',
      overflow: 'visible',
    }}>
      <div
        // Re-keyed per page so switching remounts the canvas and plays the
        // configured entry animation. Single-page tools keep a stable key.
        key={multiPageActive ? currentPageId : 'single'}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: canvasWidth,
          height: canvasHeight,
          // Keep the public page a literal zoom of the editor preview.
          zoom: ratio,
          ...(multiPageActive ? pageTransitionStyle(tool.pageSettings) : {}),
        }}>
        {/* HOOK-COUNT INVARIANT: renderBlock calls React hooks inline (hero
            slideshow, countdown), so every device-visible block must be
            rendered on every page — filtering per page would change the hook
            count when the visitor navigates and crash React ("Rendered fewer
            hooks than expected"). Off-page blocks render inside display:none
            so the wizard still shows one page at a time. */}
        {deviceBlocks.map(b => {
          const el = renderBlock(b);
          if (!el) return null;
          const onPage = !multiPageActive
            || b.type === 'background'
            || resolveBlockPageId(b.pageId, tool.pages!) === currentPageId;
          return onPage ? el : <div key={`offpage-${b.id}`} style={{ display: 'none' }}>{el}</div>;
        })}
      </div>
    </div>
  );

  // Rendered into document.body, so it costs the page nothing until a
  // submission succeeds — and it can't be clipped or zoomed by the canvas.
  const successModal = (
    <FormSuccessModal
      open={successOpen && !printMode}
      // Closing reloads the page so the next visitor (kiosk, a family
      // registering several children) gets a blank form back — clearing React
      // state alone would leave the block in its "נשלח" state. Two cases must
      // NOT reload: an ambassador signup, whose personal share link lives on
      // this page and would be lost, and a pending redirect, which a reload
      // would cancel before it fires.
      onClose={() => {
        if (ambassador || tool.redirectUrl) { setSuccessOpen(false); return; }
        window.location.reload();
      }}
      primary={primary}
      message={tool.successMessage || undefined}
      logoUrl={tool.logoUrl || undefined}
      // An ambassador signup never redirects — its share link IS the result.
      redirecting={!!tool.redirectUrl && !ambassador}
      ambassador={ambassador}
    />
  );

  const pdfViewerModal = pdfModal && typeof document !== 'undefined' && createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={pdfModal.title}
      dir="rtl"
      onClick={event => { if (event.target === event.currentTarget) setPdfModal(null); }}
      style={{ position: 'fixed', inset: 0, zIndex: 10050, background: 'rgba(15, 23, 42, 0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
    >
      <div style={{ width: 'min(960px, 100%)', height: 'min(92vh, 900px)', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 70px rgba(0,0,0,.35)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ minHeight: 58, padding: '10px 14px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <strong style={{ color: '#111827', fontSize: 16 }}>{pdfModal.title}</strong>
          <button
            type="button"
            autoFocus
            aria-label="סגירת המסמך וחזרה לטופס"
            onClick={() => setPdfModal(null)}
            style={{ border: 0, borderRadius: 10, padding: '9px 14px', background: primary, color: '#fff', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            סגור וחזור לטופס
          </button>
        </div>
        <iframe title={pdfModal.title} src={pdfModal.url} style={{ flex: 1, width: '100%', border: 0, background: '#f3f4f6' }} />
      </div>
    </div>,
    document.body,
  );

  // Preserve the exact legacy DOM shape for ordinary one-page tools (the
  // fragment adds no element, and the dialog lives in a portal).
  if (!multiPageActive) return <>{canvasElement}{successModal}{pdfViewerModal}</>;

  // Shared props for the step navigation — rendered up to twice (top/bottom)
  // per the document's pageSettings.
  const navProps = {
    pages: tool.pages!,
    currentPageId,
    onNavigate: handlePageNavigate,
    canAdvance: true,
    finalLabel: tool.submitButtonText || 'שלח',
    onFinalSubmit: () => {
      if (validateCurrentPage()) handleFormSubmit();
    },
    primaryColor: primary,
    progressStyle: pageCfg.progressStyle,
  };
  const validationBanner = validationError && (
    <div dir="rtl" style={{ maxWidth: 500, margin: '8px auto', padding: '8px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
      {validationError}
    </div>
  );
  const showTopNav = pageCfg.navPosition === 'top' || pageCfg.navPosition === 'both';
  const showBottomNav = pageCfg.navPosition === 'bottom' || pageCfg.navPosition === 'both';

  return (
    <div>
      <style>{MULTIPAGE_KEYFRAMES_CSS}</style>

      {/* Step indicator + optional top buttons */}
      {(pageCfg.progressPosition === 'top' || showTopNav) && (
        <div style={{ padding: '16px 0 8px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            <MultiStepNavigation
              {...navProps}
              showProgress={pageCfg.progressPosition === 'top'}
              showButtons={showTopNav}
            />
          </div>
        </div>
      )}

      {/* Error banner sits next to the buttons the user actually clicked —
          bottom when bottom buttons exist, else under the top bar. */}
      {!showBottomNav && validationBanner}

      {canvasElement}

      {/* Bottom buttons + optional bottom indicator */}
      {(pageCfg.progressPosition === 'bottom' || showBottomNav) && (
        <div style={{ padding: '12px 0 20px', borderTop: '1px solid #f3f4f6' }}>
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            {showBottomNav && validationBanner}
            <MultiStepNavigation
              {...navProps}
              showProgress={pageCfg.progressPosition === 'bottom'}
              showButtons={showBottomNav}
            />
          </div>
        </div>
      )}
      {successModal}
      {pdfViewerModal}
    </div>
  );
};

// ── Main ─────────────────────────────────────────────────────────────────────

// ── Signature-form Renderer (document layout with inline fields) ─────────
const InlineSigInput: React.FC<{
  field: SigInlineField;
  value: string;
  onChange: (v: string) => void;
  primary: string;
  /** select fields — reports the picked option's id alongside its label. */
  onPickOption?: (optionId: string) => void;
  /** true when the document already prints the label right above the blank —
   *  repeating it inside the input just doubles every word on the page. */
  labelled?: boolean;
}> = ({ field, value, onChange, primary, onPickOption, labelled }) => {
  const widthCh = field.width || (field.type === 'date' ? 12 : field.type === 'idNumber' ? 12 : field.type === 'number' ? 8 : 18);
  // On a phone the blanks can't sit inside the sentence — the line wraps and
  // labels end up next to the wrong blank (see .sig-doc styles). Each field
  // becomes its own row there, with a caption that survives typing.
  // No extra caption: the document's own words already name the field, and the
  // placeholder repeats it inside the empty blank — a third label just adds noise.
  const wrap = (control: React.ReactNode) => (
    <span className="sig-field-wrap">{control}</span>
  );
  // Org-decided value (e.g. a registration link pinned to one class) — printed
  // into the document as text so the signed copy still states it.
  if (field.readOnly) {
    return <span className="font-bold mx-1" style={{ color: primary }}>{value || '—'}</span>;
  }
  if (field.type === 'select') {
    const options = field.options || [];
    return wrap(
      <select
        value={value || ''}
        onChange={e => {
          const opt = options.find(o => o.label === e.target.value);
          onChange(e.target.value);
          onPickOption?.(opt?.id || '');
        }}
        required={field.required}
        style={{ minWidth: `${Math.max(widthCh, 12)}ch`, borderBottomColor: value ? primary : undefined }}
        className="sig-field inline-block border-0 border-b-2 border-gray-400 focus:border-current bg-transparent px-1 py-0.5 text-sm outline-none mx-1 align-baseline"
      >
        <option value="">{labelled ? '— בחר —' : `— ${field.label} —`}</option>
        {options.map(o => <option key={o.id} value={o.label}>{o.label}</option>)}
      </select>,
    );
  }
  // Hebrew-calendar date fields swap the native picker for the popover variant —
  // the value stays Gregorian ISO, only the way it's picked and shown changes.
  if (field.type === 'date' && (field.calendar === 'hebrew' || field.calendar === 'both')) {
    return wrap(
      <InlineHebrewDate
        value={value || ''}
        onChange={onChange}
        label={labelled ? 'בחר תאריך' : field.label}
        primary={primary}
        mode={field.calendar}
        widthCh={field.width || (field.calendar === 'both' ? 22 : 14)}
      />,
    );
  }
  const inputType = field.type === 'date' ? 'date' : 'text';
  const inputMode: any = field.type === 'idNumber' || field.type === 'number' ? 'numeric' : undefined;
  return wrap(
    <input
      type={inputType}
      inputMode={inputMode}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      required={field.required}
      placeholder={labelled ? '' : field.label}
      style={{ width: `${widthCh}ch`, minWidth: '6ch', borderBottomColor: value ? primary : undefined }}
      className="sig-field inline-block border-0 border-b-2 border-gray-400 focus:border-current bg-transparent px-1 py-0.5 text-sm outline-none mx-1 align-baseline"
    />,
  );
};

// Formatting the org applied to PART of a paragraph travels as html; only the
// tags/styles our editor produces are honored, everything else flattens to text.
const RICH_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'SPAN', 'FONT', 'DIV', 'P', 'BR']);

const richStyleOf = (el: HTMLElement): React.CSSProperties => {
  const s = el.style;
  const out: React.CSSProperties = {};
  if (s.color) out.color = s.color;
  if (s.fontSize) out.fontSize = s.fontSize;
  if (s.fontWeight) out.fontWeight = s.fontWeight as any;
  if (s.fontStyle) out.fontStyle = s.fontStyle as any;
  if (s.textDecoration || s.textDecorationLine) out.textDecoration = (s.textDecoration || s.textDecorationLine) as any;
  if (s.backgroundColor) out.backgroundColor = s.backgroundColor;
  const legacy = el.getAttribute('color');
  if (legacy && !out.color) out.color = legacy;
  return out;
};

const renderSigRichParagraph = (
  html: string,
  renderField: (fieldId: string, key: number) => React.ReactNode,
): React.ReactNode[] => {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return [];
  let key = 0;
  const walk = (node: Node): React.ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const el = node as HTMLElement;
    const fid = el.getAttribute('data-field-id');
    if (fid) return renderField(fid, key++);
    if (el.tagName === 'BR') return <br key={key++} />;
    const kids = Array.from(el.childNodes).map(walk);
    if (!RICH_TAGS.has(el.tagName)) return <React.Fragment key={key++}>{kids}</React.Fragment>;
    const css: React.CSSProperties = {
      ...richStyleOf(el),
      ...(el.tagName === 'B' || el.tagName === 'STRONG' ? { fontWeight: 'bold' as const } : {}),
      ...(el.tagName === 'I' || el.tagName === 'EM' ? { fontStyle: 'italic' as const } : {}),
      ...(el.tagName === 'U' ? { textDecoration: 'underline' } : {}),
    };
    return el.tagName === 'DIV' || el.tagName === 'P'
      ? <div key={key++} style={css}>{kids}</div>
      : <span key={key++} style={css}>{kids}</span>;
  };
  return Array.from(root.childNodes).map(walk);
};

const renderSigParagraph = (
  text: string,
  fields: SigInlineField[],
  values: Record<string, any>,
  setValue: (id: string, v: string) => void,
  primary: string,
  onPickOption?: (fieldId: string, optionId: string) => void,
) => {
  const map = new Map(fields.map(f => [f.id, f]));
  const out: React.ReactNode[] = [];
  const re = /\{\{([a-zA-Z0-9_-]+)\}\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  // Consecutive blanks become ONE row of fields — same baseline, same height,
  // labels above. Free text between them (the sentence itself) closes the row.
  let row: React.ReactNode[] = [];
  const flushRow = () => {
    if (!row.length) return;
    out.push(<span key={key++} className="sig-row">{row}</span>);
    row = [];
  };
  while ((m = re.exec(text)) !== null) {
    const id = m[1];
    const before = m.index > last ? text.slice(last, m.index) : '';
    const f = map.get(id);
    if (!f) {
      if (before) { flushRow(); out.push(<span key={key++}>{before}</span>); }
      flushRow();
      out.push(<span key={key++} className="text-red-500">{m[0]}</span>);
      last = m.index + m[0].length;
      continue;
    }
    // The words right before a blank are its label ("תאריך לידה ____") and
    // travel with it; whatever comes before that is sentence text.
    const { lead, label } = splitFieldLabel(before);
    if (lead.trim()) { flushRow(); out.push(<span key={key++} className="sig-lead">{lead}</span>); }
    // Width comes from the field the org defined, so the columns keep the
    // proportions of the document (a ת"ז blank stays narrower than a name).
    const widthCh = f.width || (f.type === 'date' ? 12 : f.type === 'idNumber' ? 12 : f.type === 'number' ? 8 : 18);
    row.push(
      <span key={key++} className="sig-pair" style={{ width: `${Math.max(widthCh, label.length + 1)}ch` }}>
        <span className="sig-pair-label">{label || field_label(f)}</span>
        <InlineSigInput field={f} value={values[id]} onChange={v => setValue(id, v)} primary={primary}
          labelled
          onPickOption={optId => onPickOption?.(id, optId)} />
      </span>,
    );
    last = m.index + m[0].length;
  }
  flushRow();
  if (last < text.length && text.slice(last).trim()) out.push(<span key={key++} className="sig-lead">{text.slice(last)}</span>);
  return out;
};

/** Fallback caption when the sentence gave the blank no words of its own. */
const field_label = (f: SigInlineField) => f.label || '';

/**
 * Splits the text before a blank into what stays in the sentence (`lead`) and
 * the short label that belongs to the blank (`label`). The label is the tail
 * after the last separator — up to 3 words / 26 chars — and when that tail is
 * empty (text ends with ":") the separator itself joins the label.
 */
const splitFieldLabel = (before: string): { lead: string; label: string } => {
  if (!before) return { lead: '', label: '' };
  if (/\n\s*$/.test(before)) return { lead: before, label: '' }; // blank starts its own line
  const sepIdx = Math.max(
    before.lastIndexOf(':'), before.lastIndexOf(','), before.lastIndexOf('·'),
    before.lastIndexOf(';'), before.lastIndexOf(')'), before.lastIndexOf('('),
    before.lastIndexOf('\n'),
  );
  const tail = sepIdx >= 0 ? before.slice(sepIdx + 1) : before;
  const words = tail.trim().split(/\s+/).filter(Boolean);
  const ok = (w: string[]) => w.length > 0 && w.length <= 3 && w.join(' ').length <= 26;
  if (ok(words)) {
    const label = words.join(' ');
    return { lead: before.slice(0, before.length - label.length - (tail.endsWith(' ') ? 1 : 0)), label };
  }
  // "…:" with nothing after it → the whole short phrase is the caption
  // ("שכבה / כיתה מבוקשת:"), never a fragment of it
  if (!words.length && sepIdx >= 0) {
    const head = before.trim();
    if (head.length <= 30) return { lead: '', label: head };
    const hw = head.split(/\s+/).filter(Boolean);
    const take = hw.slice(-3).join(' ');
    if (take.length <= 30) return { lead: head.slice(0, head.length - take.length), label: take };
  }
  // long tail → keep only the last couple of words with the blank
  const take = words.slice(-2).join(' ');
  if (take && take.length <= 26) return { lead: before.slice(0, before.length - take.length - 1), label: take };
  return { lead: before, label: '' };
};

const SignatureFormRenderer: React.FC<{
  tool: PublicToolData;
  primary: string;
  submitted: boolean;
  onSuccess: (ambassador?: AmbassadorResult | null) => void;
  /** Ambassador-signup result, when this form is linked to a campaign. */
  ambassador?: AmbassadorResult | null;
  slug: string;
}> = ({ tool, primary, submitted, onSuccess, slug }) => {
  // Prefill from PDF render: window.__PREFILL_VALUES__ = full submission data
  // (the same shape the public submit endpoint stores: { values, checkboxes,
  // signature, signatures }). Used by Puppeteer to render a printable copy.
  const { prefillData, printMode } = React.useMemo(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const isPrint = params.get('print') === '1' || params.get('printMode') === '1';
    const injected = typeof window !== 'undefined' ? (window as any).__PREFILL_VALUES__ : null;
    return { prefillData: injected && typeof injected === 'object' ? injected : null, printMode: isPrint };
  }, []);

  const [values, setValues] = useState<Record<string, any>>(prefillData?.values || {});
  const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>(prefillData?.checkboxes || {});
  const [signatures, setSignatures] = useState<Record<string, string>>(() => {
    if (!prefillData) return {};
    // Prefer per-block signatures map; fall back to flat signature applied
    // to the first signature block so older single-signature submissions
    // still render their image in the PDF.
    if (prefillData.signatures && typeof prefillData.signatures === 'object') return prefillData.signatures;
    if (prefillData.signature && typeof prefillData.signature === 'string') {
      const sigBlock = (tool.documentBlocks || []).find(b => b.type === 'signature');
      return sigBlock ? { [sigBlock.id]: prefillData.signature } : {};
    }
    return {};
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // set when the institution asked for payment straight after signing
  const [payAfterSign, setPayAfterSign] = useState<{
    url: string; amount: number; lines?: string[];
    total?: number;
    payments?: { name: string; url: string; amount: number; percent: number; lines?: string[] }[];
  } | null>(null);

  const docBlocks = tool.documentBlocks || [];
  const rawInlineFields = tool.inlineFields || [];
  // The unit id behind the class the signer picked (its label is what the
  // document shows and stores).
  const [regUnitId, setRegUnitId] = useState<string>('');

  // Tuition registration forms: the class chip becomes a picker over the
  // institution's real classes, or plain text pinned by the org — free text
  // ("ג1" / "ג' - 1" / "ג-1" for one class) can't be matched to anything.
  const regMode = tool.regClassMode || 'free';
  const regOptions = tool.regClassOptions || [];
  // Payment track (מסלול תשלום): same three shapes, over the institution's
  // plan templates. What the signer picks is what approval bills.
  const planMode = tool.regPlanMode || 'none';
  const planOptions = tool.regPlanOptions || [];
  const [regPlanTemplateId, setRegPlanTemplateId] = useState<string>('');
  // מי משלם — the form collects money right after signing, so the signer says
  // who pays and (for divorced parents) how the bill is split.
  // 'first' and 'student' pin the payer — nothing to ask
  const payerChoiceOn = !!tool.regPaymentEnabled && (tool.regPayerMode || 'choice') === 'choice';
  const [payerMode, setPayerMode] = useState<'p0' | 'p1' | 'split' | 'student'>('p0');
  const [splitKind, setSplitKind] = useState<'percent' | 'amount'>('percent');
  const [splitP0, setSplitP0] = useState('50');
  const [splitP1, setSplitP1] = useState('50');
  const inlineFields = React.useMemo(() => rawInlineFields.map(f => {
    if (f.id === 'reg_plan') {
      // pinned by the org → read-only; otherwise the signer picks a track
      if (planMode === 'fixed') return { ...f, readOnly: true, required: false };
      if (planMode === 'choice' && planOptions.length) return { ...f, type: 'select' as const, options: planOptions };
      return f;
    }
    if (f.id !== 'reg_class') return f;
    if (regMode === 'fixed') return { ...f, readOnly: true, required: false };
    // No classes defined yet → leave it a plain text field rather than an
    // empty dropdown nobody can satisfy.
    if (regMode === 'choice' && regOptions.length) return { ...f, type: 'select' as const, options: regOptions };
    return f;
  }), [rawInlineFields, regMode, regOptions, planMode, planOptions]);

  // Pinned class: fill the value so the signed copy states it, and send its id.
  useEffect(() => {
    if (regMode !== 'fixed') return;
    setRegUnitId(String(tool.regFixedUnitId || ''));
    if (tool.regFixedUnitName) setValues(prev => (prev.reg_class ? prev : { ...prev, reg_class: tool.regFixedUnitName! }));
  }, [regMode, tool.regFixedUnitId, tool.regFixedUnitName]);

  // Pinned track: same — the signed copy must state the terms being agreed to.
  useEffect(() => {
    if (planMode !== 'fixed') return;
    setRegPlanTemplateId(String(tool.regFixedPlanTemplateId || ''));
    if (tool.regFixedPlanLabel) setValues(prev => (prev.reg_plan ? prev : { ...prev, reg_plan: tool.regFixedPlanLabel! }));
  }, [planMode, tool.regFixedPlanTemplateId, tool.regFixedPlanLabel]);

  // Only fields actually placed in the document can be filled — a required
  // field whose chip was deleted from the text used to block the submit with
  // an error naming a field the signer could not see anywhere.
  const fieldById = React.useMemo(() => new Map(inlineFields.map(f => [f.id, f])), [inlineFields]);

  const placedFieldIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const b of docBlocks) {
      const re = /\{\{([a-zA-Z0-9_-]+)\}\}/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(b.text || '')) !== null) ids.add(m[1]);
    }
    return ids;
  }, [docBlocks]);

  /** What the signer chose about who pays — percentages, or ₪ of the first
   *  payment. The server re-validates and normalises it (it owns the totals). */
  const paymentSplitPayload = () => {
    if (!payerChoiceOn) return undefined;
    // the student pays for himself — the link goes to the phone he typed
    if (payerMode === 'student') return { mode: 'student', shares: [] };
    if (payerMode === 'p0') return { mode: 'single', shares: [{ idx: 0, percent: 100 }] };
    if (payerMode === 'p1') return { mode: 'single', shares: [{ idx: 1, percent: 100 }] };
    const a = Number(splitP0) || 0;
    const b = Number(splitP1) || 0;
    if (a <= 0 && b <= 0) return { mode: 'single', shares: [{ idx: 0, percent: 100 }] };
    return splitKind === 'percent'
      ? { mode: 'percent', shares: [{ idx: 0, percent: a }, { idx: 1, percent: b }].filter(x => x.percent > 0) }
      : { mode: 'amount', shares: [{ idx: 0, amount: a }, { idx: 1, amount: b }].filter(x => x.amount > 0) };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    for (const f of inlineFields) {
      if (f.required && placedFieldIds.has(f.id) && !values[f.id]) { setError(`שדה חובה: ${f.label}`); return; }
    }
    for (const b of docBlocks) {
      if (b.type === 'checkbox' && b.required && !checkboxes[b.id]) {
        setError('יש לסמן את כל הסעיפים הנדרשים'); return;
      }
    }
    const sigBlocks = docBlocks.filter(b => b.type === 'signature');
    const requiredSigs = sigBlocks.filter(b => b.required);
    for (const b of requiredSigs) {
      if (!signatures[b.id]) { setError('נדרשת חתימה'); return; }
    }

    // Combine all signatures into a single canonical "signature" field
    // (uses the first signature block's data); all are kept under signatures{}.
    const primarySig = sigBlocks.map(b => signatures[b.id]).find(Boolean) || '';

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/public/tools/${slug}/submit`, {
        data: { values, checkboxes, signatures, signature: primarySig },
        // Kept out of `values` on purpose: the document prints its labels, and
        // an id in there would show up in the signed copy and the submissions
        // table. The server re-validates it against the institution anyway.
        regUnitId: regUnitId || undefined,
        regPlanTemplateId: regPlanTemplateId || undefined,
        paymentSplit: paymentSplitPayload(),
        mc: getMcParam(),
      });
      // The institution may collect payment right after signing — hold the link
      // so the success screen can send the signer straight to it.
      if (res?.data?.payment?.url) setPayAfterSign(res.data.payment);
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'שגיאה בשליחה');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4" dir="rtl">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 max-w-md w-full text-center">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">הטופס נחתם!</h2>
          <p className="text-gray-500">{tool.successMessage || 'תודה רבה. הטופס התקבל ונשמר במערכת.'}</p>
          {payAfterSign && (
            <div className="mt-5 pt-5 border-t border-gray-200">
              <p className="text-sm font-bold text-gray-800 mb-1">נותר להסדיר את התשלום</p>
              {/* split bill → one button per parent, each opening their own link */}
              {(payAfterSign.payments || []).length > 1 ? (
                <>
                  <p className="text-xs text-gray-500 mb-2">
                    התשלום מחולק בין ההורים — כל אחד משלם את חלקו
                    {payAfterSign.total ? ` (סה"כ ₪${Math.round(payAfterSign.total).toLocaleString()})` : ''}
                  </p>
                  {(payAfterSign.payments || []).map((pmt, i) => (
                    <div key={i} className="mb-3 text-right">
                      {(pmt.lines || []).map((l, j) => (
                        <p key={j} className="text-[11px] text-gray-500">{l}</p>
                      ))}
                      <a href={pmt.url}
                        className="mt-1.5 inline-block w-full text-white font-bold px-6 py-3 rounded-xl shadow text-center"
                        style={{ background: primary }}>
                        {pmt.name || 'תשלום'} · ₪{Math.round(pmt.amount).toLocaleString()}
                        {pmt.percent ? ` (${pmt.percent}%)` : ''}
                      </a>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {(payAfterSign.lines || []).map((l, i) => (
                    <p key={i} className="text-xs text-gray-500">{l}</p>
                  ))}
                  <a href={payAfterSign.url}
                    className="mt-3 inline-block w-full text-white font-bold px-6 py-3 rounded-xl shadow"
                    style={{ background: primary }}>
                    מעבר לתשלום · ₪{Math.round(payAfterSign.amount).toLocaleString()}
                  </a>
                </>
              )}
              <p className="text-[11px] text-gray-400 mt-2">נשלח אליכם גם קישור ב-SMS למקרה שתסגרו את הדף.</p>
            </div>
          )}
          <p className="text-gray-400 text-sm mt-2">{tool.name}</p>
        </div>
      </div>
    );
  }

  const docBackground: React.CSSProperties = tool.docBgImage
    ? { background: `url(${tool.docBgImage}) center/cover no-repeat, ${tool.docBgColor || '#ffffff'}` }
    : { background: tool.docBgColor || '#ffffff' };

  // Mark the form ready for the PDF renderer to capture. When prefilled (PDF
  // path), the marker fires immediately; for live users it doesn't matter.
  const printReady = printMode ? '1' : undefined;

  return (
    <div className="sig-doc min-h-screen bg-gradient-to-br from-gray-200 to-gray-300 py-6 px-3" dir="rtl" style={{ fontFamily: "'Heebo', sans-serif" }} data-print-ready={printReady}>
      {/* A fill-in-the-blanks document only reads as one on a wide screen. On a
          phone the sentence wraps and every blank lands next to the wrong
          label, so each field gets its own captioned row instead. The printed
          PDF renders at desktop width, so it keeps the document look. */}
      <style>{`
        /* Blanks are laid out as a real form row: labels above, every control
           the same height, bottoms aligned — so the page reads as a form
           instead of underscores scattered through a paragraph. */
        .sig-doc .sig-lead { display: block; }
        .sig-doc .sig-row {
          display: flex; flex-wrap: wrap; align-items: flex-end;
          gap: 10px 22px; margin: 8px 0 4px;
        }
        .sig-doc .sig-pair {
          display: flex; flex-direction: column; justify-content: flex-end;
          max-width: 100%; min-width: 8ch;
        }
        .sig-doc .sig-pair-label {
          font-size: 0.78em; font-weight: 600; color: #6b7280;
          line-height: 1.4; margin-bottom: 2px; white-space: nowrap;
          overflow: hidden; text-overflow: ellipsis;
        }
        .sig-doc .sig-pair .sig-field,
        .sig-doc .sig-pair .sig-field-btn {
          display: block !important; width: 100% !important; min-width: 0 !important;
          height: 34px; line-height: 1.4; padding: 2px 6px;
          margin-inline: 0 !important; box-sizing: border-box; text-align: start;
        }
        .sig-doc .sig-pair .sig-field-wrap { display: block; }
        .sig-doc p { line-height: 1.9; }
        @media (max-width: 640px) {
          .sig-doc .sig-lead { display: block; }
        .sig-doc .sig-row { flex-direction: column; align-items: stretch; gap: 12px; }
          .sig-doc .sig-pair { width: 100% !important; }
          .sig-doc .sig-pair-label { font-size: 0.85em; }
          .sig-doc .sig-field-wrap { display: block; margin: 4px 0 12px; }
          .sig-doc .sig-field,
          .sig-doc .sig-field-btn {
            display: block !important; width: 100% !important; min-width: 0 !important;
            margin-inline: 0 !important; text-align: start; box-sizing: border-box;
          }
        }
      `}</style>
      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto shadow-2xl rounded-lg overflow-hidden" style={docBackground}>
        <div className="border-b-2 border-gray-200 px-4 sm:px-8 py-6 flex items-center justify-between">
          <div style={{ textAlign: tool.logoAlign || 'right', flex: '0 1 auto' }}>
            {tool.logoUrl && (
              <img src={tool.logoUrl} alt="לוגו"
                style={{ height: tool.logoHeight || 48, width: 'auto', objectFit: 'contain' }}
                className="inline-block mb-1" />
            )}
          </div>
          <div className="text-left">
            <p className="text-xs text-gray-400">בס״ד</p>
            <p className="text-xs text-gray-500 mt-1">תאריך: {new Date().toLocaleDateString('he-IL')}</p>
          </div>
        </div>

        {tool.title && (
          <div className="px-8 pt-8 pb-2">
            <h1 className="font-bold text-gray-900"
              style={{
                fontSize: tool.titleStyle?.fontSize ?? 30,
                fontWeight: tool.titleStyle?.fontWeight ?? 'bold',
                color: tool.titleStyle?.color ?? undefined,
                textDecoration: tool.titleStyle?.underline ? 'underline' : undefined,
                fontStyle: tool.titleStyle?.italic ? 'italic' : undefined,
                textAlign: tool.titleStyle?.align ?? 'center',
              }}>
              {tool.title}
            </h1>
            {tool.description && (
              <p className="mt-1"
                style={{
                  fontSize: tool.descriptionStyle?.fontSize ?? 14,
                  color: tool.descriptionStyle?.color ?? '#6b7280',
                  fontWeight: tool.descriptionStyle?.fontWeight ?? undefined,
                  textDecoration: tool.descriptionStyle?.underline ? 'underline' : undefined,
                  fontStyle: tool.descriptionStyle?.italic ? 'italic' : undefined,
                  textAlign: tool.descriptionStyle?.align ?? 'center',
                }}>
                {tool.description}
              </p>
            )}
          </div>
        )}

        <div className="px-4 sm:px-8 py-6 space-y-5 text-gray-800 leading-relaxed">
          {docBlocks.map(block => {
            const s = block.style || {};
            const styleCss: React.CSSProperties = {
              fontSize: s.fontSize ?? undefined,
              fontWeight: s.fontWeight ?? undefined,
              color: s.color ?? undefined,
              textDecoration: s.underline ? 'underline' : undefined,
              fontStyle: s.italic ? 'italic' : undefined,
              textAlign: s.align ?? undefined,
            };
            switch (block.type) {
              case 'heading':
                return (
                  <h2 key={block.id} style={styleCss}
                    className={block.level === 1 ? 'text-xl font-bold border-b border-gray-300 pb-1' : 'text-lg font-semibold mt-2'}>
                    {block.text}
                  </h2>
                );
              case 'paragraph':
                return (
                  <p key={block.id} style={styleCss} className="text-sm md:text-base whitespace-pre-wrap">
                    {block.html
                      ? renderSigRichParagraph(block.html, (fid, key) => {
                        const f = fieldById.get(fid);
                        if (!f) return <span key={key} className="text-red-500">{`{{${fid}}}`}</span>;
                        return (
                          <InlineSigInput key={key} field={f} value={values[fid]} primary={primary}
                            onChange={v => setValues(prev => ({ ...prev, [fid]: v }))}
                            onPickOption={optId => {
                              if (fid === 'reg_class') setRegUnitId(optId);
                              if (fid === 'reg_plan') setRegPlanTemplateId(optId);
                            }} />
                        );
                      })
                      : renderSigParagraph(block.text || '', inlineFields, values,
                        (id, v) => setValues(prev => ({ ...prev, [id]: v })), primary,
                        (id, optId) => {
                          if (id === 'reg_class') setRegUnitId(optId);
                          if (id === 'reg_plan') setRegPlanTemplateId(optId);
                        })}
                  </p>
                );
              case 'image':
                if (!block.src) return null;
                return (
                  <div key={block.id} style={{ textAlign: block.align || 'center' }}>
                    <img src={block.src} alt=""
                      style={{ width: `${block.width || 100}%`, maxWidth: '100%', display: 'inline-block' }}
                      className="rounded" />
                  </div>
                );
              case 'checkbox':
                return (
                  <label key={block.id} className="flex items-start gap-2.5 cursor-pointer text-sm md:text-base" style={styleCss}>
                    <input type="checkbox" checked={!!checkboxes[block.id]}
                      onChange={e => setCheckboxes(prev => ({ ...prev, [block.id]: e.target.checked }))}
                      className="mt-1 w-4 h-4" style={{ accentColor: primary }} />
                    <span>{block.text}{block.required && <span className="text-red-500 mr-1">*</span>}</span>
                  </label>
                );
              case 'signature':
                return (
                  <div key={block.id} className="pt-4">
                    <p className="text-sm font-semibold mb-2">
                      {block.signerLabel || 'חתימה'}{block.required && <span className="text-red-500 mr-1">*</span>}
                    </p>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white" style={{ height: 180 }}>
                      <SignatureCanvas
                        value={signatures[block.id] || ''}
                        onChange={(dataUrl) => setSignatures(prev => ({ ...prev, [block.id]: dataUrl || '' }))}
                        props={{ penColor: '#111827', penWidth: 2, labelFontSize: 14 }}
                        width={680}
                        height={180}
                        printMode={printMode}
                      />
                    </div>
                  </div>
                );
              case 'spacer':
                return <div key={block.id} className="h-6" />;
              default:
                return null;
            }
          })}

          {/* Class picker for documents whose {{reg_class}} chip was removed
              from the text — without it a "choice" form would have nowhere to
              pick, and every registration would land unassigned. */}
          {regMode === 'choice' && !placedFieldIds.has('reg_class') && (printMode || regOptions.length > 0) && (
            <div className="pt-2">
              <p className="text-sm font-semibold mb-1.5">שכבה / כיתה</p>
              {printMode ? (
                <p className="text-sm font-bold" style={{ color: primary }}>{values.reg_class || '—'}</p>
              ) : (
              <select
                value={values.reg_class || ''}
                onChange={e => {
                  const label = e.target.value;
                  setValues(prev => ({ ...prev, reg_class: label }));
                  setRegUnitId(regOptions.find(o => o.label === label)?.id || '');
                }}
                className="w-full max-w-xs border-2 border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-current bg-white"
                style={{ color: values.reg_class ? primary : undefined }}
              >
                <option value="">— בחר —</option>
                {regOptions.map(o => <option key={o.id} value={o.label}>{o.label}</option>)}
              </select>
              )}
            </div>
          )}

          {/* Safety net for a document with no {{reg_plan}} chip: a pinned track
              is stated as text, a choice form still gets its picker. */}
          {planMode !== 'none' && !placedFieldIds.has('reg_plan') && (printMode || planMode === 'fixed' || planOptions.length > 0) && (
            <div className="pt-2">
              <p className="text-sm font-semibold mb-1">מסלול תשלום</p>
              {printMode || planMode === 'fixed' ? (
                <p className="text-sm font-bold" style={{ color: primary }}>{values.reg_plan || tool.regFixedPlanLabel || '—'}</p>
              ) : (
                <select
                  value={values.reg_plan || ''}
                  onChange={e => {
                    const label = e.target.value;
                    setValues(prev => ({ ...prev, reg_plan: label }));
                    setRegPlanTemplateId(planOptions.find(o => o.label === label)?.id || '');
                  }}
                  className="w-full max-w-md border-2 border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-current bg-white"
                  style={{ color: values.reg_plan ? primary : undefined }}
                >
                  <option value="">— בחר מסלול —</option>
                  {planOptions.map(o => <option key={o.id} value={o.label}>{o.label}</option>)}
                </select>
              )}
            </div>
          )}

          {/* מי משלם — only when the institution collects payment right after
              signing. With a second parent on the form the bill can be split
              (אחוזים / ₪) and each parent gets their own payment link. */}
          {payerChoiceOn && !printMode && (
            <div className="pt-3 border-t border-gray-200">
              <p className="text-sm font-semibold mb-1.5">מי משלם?</p>
              <div className="flex flex-wrap gap-2">
                {([
                  ['p0', (values.reg_parent_name || '').trim() || 'הורה 1'],
                  ...((values.reg_parent2_name || '').trim() ? [['p1', (values.reg_parent2_name || '').trim()]] : []),
                  ...((values.reg_parent2_name || '').trim() ? [['split', 'שניהם — חלוקה']] : []),
                  ...(tool.regPayerAllowStudent
                    ? [['student', `התלמיד/ה${(values.reg_student_first || '').trim() ? ` — ${(values.reg_student_first || '').trim()}` : ''} (משלם/ת בעצמו/ה)`]]
                    : []),
                ] as [string, string][]).map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setPayerMode(v as any)}
                    className={`px-3 py-2 text-sm font-bold rounded-xl border-2 transition ${payerMode === v ? 'text-white' : 'bg-white text-gray-600 border-gray-300'}`}
                    style={payerMode === v ? { background: primary, borderColor: primary } : undefined}>
                    {l}
                  </button>
                ))}
              </div>

              {payerMode === 'split' && (
                <div className="mt-3 space-y-2">
                  <div className="flex bg-gray-100 rounded-lg p-0.5 w-fit">
                    {([['percent', 'לפי אחוזים'], ['amount', 'לפי סכום']] as const).map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setSplitKind(v)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md ${splitKind === v ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                        style={splitKind === v ? { color: primary } : undefined}>{l}</button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {([
                      [(values.reg_parent_name || 'הורה 1'), splitP0, setSplitP0],
                      [(values.reg_parent2_name || 'הורה 2'), splitP1, setSplitP1],
                    ] as [string, string, (v: string) => void][]).map(([label, val, set], i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-600 font-semibold">{label}</span>
                        <input type="number" min={0} inputMode="decimal" value={val}
                          onChange={e => {
                            set(e.target.value);
                            // percentages are complementary — typing 60 for one
                            // parent leaves 40 for the other, no arithmetic
                            if (splitKind === 'percent') {
                              const other = Math.max(0, 100 - (Number(e.target.value) || 0));
                              (i === 0 ? setSplitP1 : setSplitP0)(String(other));
                            }
                          }}
                          className="w-20 border-2 border-gray-300 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-current bg-white" dir="ltr" />
                        <span className="text-xs text-gray-500">{splitKind === 'percent' ? '%' : '₪'}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400">
                    {splitKind === 'percent'
                      ? 'החלוקה חלה גם על דמי הרישום וגם על התשלום החודשי — כל הורה מקבל קישור תשלום נפרד.'
                      : 'הסכומים הם חלקו של כל הורה בתשלום הראשון; אותה חלוקה תחול על התשלומים החודשיים.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">⚠️ {error}</div>}
        </div>

        {!printMode && (
          <div className="px-4 sm:px-8 py-5 bg-gray-50 border-t border-gray-200 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <p className="text-xs text-gray-400 text-center sm:text-right">בלחיצה על הכפתור הינך מאשר את תוכן הטופס</p>
            <button type="submit" disabled={submitting}
              className="w-full sm:w-auto text-white font-bold px-8 py-3 rounded-xl shadow disabled:opacity-50 transition-all whitespace-nowrap"
              style={{ background: primary }}>
              {submitting ? 'שולח...' : (tool.submitButtonText || 'אישור וחתימה ✓')}
            </button>
          </div>
        )}
      </form>
      {!(tool as any).hidePlatformBranding && (
        <p className="text-center text-gray-500 text-xs mt-4">מופעל ע"י Amutech</p>
      )}
    </div>
  );
};

// slugOverride: מצב דומיין-לקוח — השורש ('/') מרנדר דף נחיתה ראשי בלי
// שה-slug יופיע בנתיב (App.tsx → CustomDomainRoot).
const PublicTool: React.FC<{ slugOverride?: string }> = ({ slugOverride }) => {
  const { slug: slugParam } = useParams<{ slug: string }>();
  const slug = slugOverride || slugParam;
  const [tool, setTool] = useState<PublicToolData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  // Set only for ambassador-signup forms — carries the personal share link the
  // submitter just earned (see AmbassadorSharePanel).
  const [ambassador, setAmbassador] = useState<AmbassadorResult | null>(null);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API_BASE}/public/tools/${slug}`);
        setTool(data);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'הכלי לא נמצא');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
      <div className="text-center">
        <div className="w-14 h-14 rounded-full border-4 border-gray-200 border-t-blue-500 animate-spin mx-auto mb-4" />
        <p className="text-gray-500 font-medium">טוען...</p>
      </div>
    </div>
  );

  if (error || !tool) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
      <div className="text-center p-10 bg-white rounded-3xl shadow-xl max-w-sm mx-4">
        <p className="text-5xl mb-4">😔</p>
        <p className="text-gray-600 text-lg font-medium">{error || 'הדף לא נמצא'}</p>
      </div>
    </div>
  );

  const primary = tool.primaryColor || '#1565C0';
  const onSuccess = (amb?: AmbassadorResult | null) => {
    setSubmitted(true);
    if (amb) setAmbassador(amb);
  };

  // Signature forms — render the document-style layout with inline fields & signature pad
  if (tool.type === 'signature') {
    return (
      <SignatureFormRenderer tool={tool} primary={primary} submitted={submitted} onSuccess={onSuccess} slug={slug || ''} />
    );
  }

  // New builder blocks — render with NewBlocksRenderer regardless of tool type
  if (hasNewBlocks(tool)) {
    return (
      <div className={`min-h-screen bg-white ${(tool as any).hidePlatformBranding ? '' : 'pb-28 sm:pb-0'}`} dir="rtl" style={{ fontFamily: "'Heebo', sans-serif" }}>
        {!(tool as any).hidePlatformBranding && <PoweredByAmutech />}
        <NewBlocksRenderer tool={tool as any} primary={primary} submitted={submitted} onSuccess={onSuccess} ambassador={ambassador} />
        {!(tool as any).hidePlatformBranding && (
          <footer className="border-t border-gray-100 py-6 text-center">
            <p className="text-xs text-gray-400">מופעל ע"י <strong className="text-gray-500">Amutech</strong></p>
          </footer>
        )}
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700;800;900&display=swap');`}</style>
      </div>
    );
  }

  if (submitted && tool.type === 'form') return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4" dir="rtl">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
        {tool.logoUrl && (
          <img src={tool.logoUrl} alt="לוגו" className="h-12 object-contain mx-auto mb-4" />
        )}
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg"
          style={{ background: `linear-gradient(135deg, ${primary}, ${primary}CC)` }}
        >
          <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-black text-gray-800 mb-2">הטופס נשלח! 🎉</h2>
        <p className="text-gray-500 leading-relaxed">
          {tool.successMessage || 'תודה! הטופס התקבל בהצלחה.'}
        </p>
        {ambassador && <AmbassadorSharePanel amb={ambassador} primary={primary} />}
        {tool.redirectUrl && !ambassador && (
          <p className="text-xs text-gray-400 mt-4">מועבר/ת בקרוב...</p>
        )}
      </div>
    </div>
  );

  if (tool.type === 'form') return (
    <div
      className="min-h-screen flex items-start justify-center py-12 px-4"
      dir="rtl"
      style={{
        background: `linear-gradient(135deg, ${primary}10 0%, white 40%, ${primary}05 100%)`,
        fontFamily: "'Heebo', sans-serif",
      }}
    >
      <div className="w-full max-w-lg">
        {tool.logoUrl && (
          <div className="text-center mb-6">
            <img src={tool.logoUrl} alt="לוגו" className="h-14 object-contain mx-auto" />
          </div>
        )}

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="px-8 pt-8 pb-5" style={{ background: `linear-gradient(135deg, ${primary}15, ${primary}05)` }}>
            <h1 className="text-2xl font-black text-gray-900 leading-tight">{tool.title || tool.name}</h1>
            {tool.description && (
              <p className="text-gray-500 text-sm mt-2 leading-relaxed">{tool.description}</p>
            )}
          </div>

          <div className="px-8 pb-8 pt-5">
            {tool.fields.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p>הטופס לא הוגדר עדיין</p>
              </div>
            ) : (
              <ToolForm tool={tool} primary={primary} onSuccess={onSuccess} />
            )}
          </div>
        </div>

        {!(tool as any).hidePlatformBranding && (
          <p className="text-center text-xs text-gray-400 mt-5">
            מופעל ע"י <strong className="text-gray-500">Amutech</strong>
          </p>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700;800;900&display=swap');
      `}</style>
    </div>
  );

  return (
    <div className="min-h-screen bg-white" dir="rtl" style={{ fontFamily: "'Heebo', sans-serif" }}>
      {hasNewBlocks(tool) ? (
        <NewBlocksRenderer tool={tool as any} primary={primary} submitted={submitted} onSuccess={onSuccess} ambassador={ambassador} />
      ) : hasLandingDesign(tool) ? (
        <LandingDesignRenderer tool={tool} primary={primary} submitted={submitted} onSuccess={onSuccess} ambassador={ambassador} />
      ) : (
        <LegacyLandingRenderer tool={tool} primary={primary} submitted={submitted} onSuccess={onSuccess} ambassador={ambassador} />
      )}

      {!(tool as any).hidePlatformBranding && (
        <footer className="border-t border-gray-100 py-6 text-center">
          <p className="text-xs text-gray-400">
            מופעל ע"י <strong className="text-gray-500">Amutech</strong>
          </p>
        </footer>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700;800;900&display=swap');
        .prose { direction: rtl; text-align: right; }
      `}</style>
    </div>
  );
};

export default PublicTool;
